import {
  withLegacyToleranceNames,
  answerSymmetry,
  angleModeFor, visibleTargeted, displayEvaluation, computeRotationAcceptedRanges, evaluateRotation, mountingFor, isRotationDefaultIncorrectUnreachable, computeRotationDefaultIncorrectGaps, isRotationFeedbackFullySubsumed, doesRotationFeedbackOverlapAccepted, findRotationOverlappingAnswerPairs, findRotationOverlappingFeedbackPairs, hasVariableChip, sanitizeTriggerValue, probeRoll, computeFormulaExtrema, triggerFault, analyseOverlapAcrossRolls, chipNamesIn, resolveTriggerValues, forEachRoll, OVERLAP_ENUMERATION_MAX_COMBOS, NO_SENSOR_DATA_TRIGGER, SET_ANSWER_TRIGGER, type SensorHealth, type GradingMode, type RotationBlockData, type FeedbackState, type RotationInterval, type RotationBoundedInterval, type TriggerFault, ROTATION_OFF_AXIS_CUTOFF_DEG} from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Trash2,
} from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useRolledWithRecalls } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { betweenInversionVerdict, type BlockIssue, incompleteBetweenRowIds, type IssueCache, type IssueContext, IssuesPanel, memoizedByKey, triggerFaultMessage, triggerRangeInversionVerdict, useIssueContext } from './block-issues';
import { RotationIcon } from './block-library';
import { BlockQuestionEditor } from './block-question-editor';
import { EmptyQuestionNote, CollapsedInputBlock, CollapsedBlockBar, MobileOnlyChip, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { answerBarHasContent, AnswerBarText, activeIntervalIds, ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass, UnreachableChip } from './eval-chip';
import { ChipDivider, FeedbackCard, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, chipEvalIcon, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { evaluateFormulaRaw } from './formula-eval';
import { FormulaField, FormulaWithValues, resolvedTriggerChip } from './formula-field';
import { gradingModes, positiveSlotTitleFor } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtDegrees, fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { SensorAdvisory } from './sensor-advisory';
import { SensorFeedbackCard } from './sensor-feedback-card';
import { SensorManualBypass } from './sensor-manual-bypass';
import { SensorAnnounce } from './sensor-readout';
import { SensorSimulateBar, type SensorScenarioOption, type SensorSourceMode } from './sensor-simulate-bar';
import { noiseForSignal, useSensorSimulator } from './sensor-simulator-context';
import { TOLERANCE_HINT_CLASS, toleranceHint } from './tolerance-hint';
import { SymmetryToggle, ToleranceRow } from './tolerance-row';
import { useRotationSensor } from './use-device-gravity';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, retriggerPositiveSlot } from './use-input-block-feedback';
import { useRestoredData } from './use-restored-data';
import { answerPositionsKey, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { BlockQuestion, BlockPreviewProvider, rollVariableValues } from './variable-chip-preview';
import { VariableTestController } from './variable-test-controller';


import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { BetweenAnswerRow } from '@/components/task/between-answer-row';
import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { SpokenPlaceholder } from '@/components/ui/spoken-placeholder';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { catchAllChips, catchAllTitle, CATCH_ALL_SOURCE, neutralChips, rotationAcceptedRangeConfig, rotationChips, formatRotationRange } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { rotationTargetedChipLabel, rotationTriggerSummary, stateChipLabel, answerExprTexts} from '@/lib/block-state-labels';
import { effectiveSetting, settingAvailable } from '@/lib/mode-gated-settings';

/** The raw bound values a trigger carries, for `resolvedTriggerChip`. Reads the stored string
 * rather than a parsed number, because the whole point is to find the formula text as authored. */
function triggerValuesOf(trigger: string): string[] {
  const parts = trigger.replace(/^[ab]:/, '').split(':');
  return parts.slice(1);
}
// ─── Types ────────────────────────────────────────────────────────────────────

interface RotationBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped by Preview's page-level Submit — see the effect that watches it. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

type RotationConditionType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'between';
type RotationTriggerType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'range';

interface ExactAnswer {
  id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
  /** Per answer since the tolerance move. Absent on answers authored before it, which fall
   *  back to the block's own field — always read through `answerSymmetry`, never directly. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
}
interface BetweenAnswer { id: string; low: string; high: string }

// The valid input domain for every target/threshold/range-bound field — fixed for this
// block (unlike slider's teacher-configurable min/max), never enforced in the underlying
// interval math (see rotation.ts's computeRotationAcceptedRanges — same as numerical.ts's/
// slider.ts's own precedent of leaving ±Infinity in the math and only bounding at the
// input-validation layer).
const ROTATION_MIN = 0;
const ROTATION_MAX = 360;
/**
 * The largest tolerance that means anything on this domain.
 *
 * Angle's domain is the closed [0, 360], so ±360 already reaches every value from any centre — beyond
 * it the accepted set is identical and the number only misleads. Compass caps at 180 for the same
 * reason expressed on a circle, where no heading is more than half a turn from another.
 *
 * A WARNING, not an error, matching Compass: evaluation copes fine, it simply accepts everything,
 * which is a consequence like feedback-subsumed rather than malformed input like an out-of-domain
 * value.
 */
const ROTATION_MAX_TOLERANCE = 360;

// Editor-local shape — every numeric field is a string during editing (matches every other
// input block in this file set), parsed only at validation/push time. Distinct name from
// @agavi/blocks' RotationBlockData (whose fields are already-parsed numbers) since this is what
// the teacher is actively typing, not what gets evaluated.
interface RotationBlockEditorData {
  /**
   * Per-field angle modes, keyed by `fieldId` — what the in-field toggle writes.
   *
   * This block has no block-wide `angleMode` and never did: the ANGLE it measures is degrees of
   * arc, which is a fact about the instrument. How a teacher's `sin()` reads its argument is a
   * separate question, and that is what this answers — per formula field, as everywhere else.
   */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  question: string;
  conditionType: RotationConditionType;
  answers: ExactAnswer[];
  toleranceSymmetry: 'symmetric' | 'asymmetric';
  /** Show the student how precisely to orient. Precision, never the angle itself — see the same
   *  field on Numerical for the rule, which is why it is offered on `exact` only. */
  showTolerance: boolean;
  betweenAnswers: BetweenAnswer[];
  threshold: string;
  gradingMode: GradingMode;
  bypassEnabled: boolean;
  feedback: { states: FeedbackState[] };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────


function num(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}


const ONE_SIDED: RotationConditionType[] = ['lt', 'lte', 'gt', 'gte'];

const CONDITION_TYPES: { id: RotationConditionType; symbol: string }[] = [
  { id: 'exact', symbol: '=' },
  { id: 'lt', symbol: '<' },
  { id: 'lte', symbol: '≤' },
  { id: 'gt', symbol: '>' },
  { id: 'gte', symbol: '≥' },
  { id: 'between', symbol: '↔' },
];

const CONDITION_ARIA_KEYS: Record<RotationConditionType, string> = {
  exact: 'conditionExact', lt: 'conditionLt', lte: 'conditionLte', gt: 'conditionGt', gte: 'conditionGte', between: 'conditionBetween',
};

// Blank, not pre-filled — a guessed value looks like a real answer and a teacher has to
// notice and clear it before typing their own; an empty field has no such trap. Matches
// slider-block.tsx's makeExactAnswer/makeBetweenAnswer convention exactly.
function makeExactAnswer(): ExactAnswer {
  return { id: crypto.randomUUID(), value: '', toleranceValue: '', toleranceAbove: '', toleranceBelow: '' };
}
function makeBetweenAnswer(): BetweenAnswer {
  return { id: crypto.randomUUID(), low: '', high: '' };
}

function parseAnswers(rawAnswers: Partial<ExactAnswer>[] | undefined): ExactAnswer[] {
  return rawAnswers?.length
    ? rawAnswers.map(a => ({
        id: a.id ?? crypto.randomUUID(), value: a.value ?? '',
        toleranceValue: a.toleranceValue ?? '', toleranceAbove: a.toleranceAbove ?? '', toleranceBelow: a.toleranceBelow ?? '',
        // Carried, never defaulted: `undefined` MEANS "fall back to the block", and a
        // literal here would strand every answer authored before the move.
        toleranceSymmetry: a.toleranceSymmetry,
      }))
    : [makeExactAnswer()];
}
function parseBetween(rawBetween: Partial<BetweenAnswer>[] | undefined): BetweenAnswer[] {
  return rawBetween?.length
    ? rawBetween.map(a => ({ id: a.id ?? crypto.randomUUID(), low: a.low ?? '', high: a.high ?? '' }))
    : [makeBetweenAnswer()];
}

function initData(rawInput: Record<string, unknown>): RotationBlockEditorData {
  /* Blocks saved before the rename say `errorValue`; read under the current names. A missed
     one would not crash — `?? ''` turns it into an empty tolerance, and an empty tolerance
     grades EXACT, so every authored band would quietly become a point. */
  const raw = withLegacyToleranceNames(rawInput);
  return {
    question: (raw.question as string) ?? '',
    conditionType: (CONDITION_TYPES.some(c => c.id === raw.conditionType) ? raw.conditionType : 'exact') as RotationConditionType,
    answers: parseAnswers(raw.answers as Partial<ExactAnswer>[] | undefined),
    toleranceSymmetry: raw.toleranceSymmetry === 'asymmetric' ? 'asymmetric' : 'symmetric',
    showTolerance: (raw.showTolerance as boolean) ?? false,
    angleModes: raw.angleModes as Record<string, 'degrees' | 'radians'> | undefined,
    betweenAnswers: parseBetween(raw.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    // Blank, not pre-filled — same "not configured yet" reasoning as every other field here.
    threshold: raw.threshold != null ? String(raw.threshold) : '',
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode : 'graded') as GradingMode,
    bypassEnabled: raw.bypassEnabled === true,
    feedback: (raw.feedback ?? { states: [] }) as RotationBlockEditorData['feedback'],
  };
}

// Same colon-delimited trigger format rotation.ts's parseValueTrigger expects ("lt:20",
// "range:5:15", etc.), applied directly to the submitted angle value.
function parseRotationTrigger(trigger: string): { type: RotationTriggerType; v1: string; v2: string } {
  const parts = trigger.split(':');
  const raw = parts[0] ?? 'lt';
  const type = (['exact', 'lt', 'lte', 'gt', 'gte', 'range'].includes(raw) ? raw : 'lt') as RotationTriggerType;
  return { type, v1: parts[1] ?? '', v2: parts[2] ?? '' };
}
// Values are sanitised, not trusted: a trigger is ONE delimited string, so a colon inside a value
// silently re-splits it into a different trigger. See sanitizeTriggerValue.
function buildRotationTrigger(type: RotationTriggerType, v1: string, v2: string): string {
  const a = sanitizeTriggerValue(v1), b = sanitizeTriggerValue(v2);
  return type === 'range' ? `range:${a}:${b}` : `${type}:${a}`;
}

const TRIGGER_TYPES: { id: RotationTriggerType; symbol: string }[] = [
  { id: 'exact', symbol: '=' }, { id: 'lt', symbol: '<' }, { id: 'lte', symbol: '≤' },
  { id: 'gt', symbol: '>' }, { id: 'gte', symbol: '≥' }, { id: 'range', symbol: '↔' },
];

const TRIGGER_ARIA_KEYS: Record<RotationTriggerType, string> = {
  exact: 'triggerTypeExact', lt: 'triggerTypeLt', lte: 'triggerTypeLte',
  gt: 'triggerTypeGt', gte: 'triggerTypeGte', range: 'triggerTypeRange',
};

// Each condition gets its own distinct phrasing (symbol + value) — matches slider.ts's own
// targeted-feedback triggerSummary exactly, not the old distance-style "Within"/"Farther
// than" pairing (which only made sense when triggers applied to a derived error, not the
// raw value directly).
// Identical to the chip label by contract (see block-state-labels.ts).
function rotationTargetedTitle(trigger: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  return rotationTargetedChipLabel(trigger, undefined, t, 'en');
}

// A block counts as "not set up yet" if it has no plausible correct answer at all — either
// nothing has been entered, or everything entered falls entirely outside [0, 360]. Mirrors
// slider-block.tsx's isAnswerSetupIncomplete exactly, with the fixed ROTATION_MIN/ROTATION_MAX in
// place of a teacher-configurable min/max.
/**
 * Is there no usable answer here yet?
 *
 * `num()` returns NaN for an equation, and NaN fails every comparison, so a perfectly good answer of
 * `{test}` was read as no answer at all — which switched off the editor's answer bar, among eight
 * other things this gates. A chip formula IS a configured answer; whether the value it resolves to
 * lands inside the domain is a question about a ROLL, and the per-roll validation already asks it
 * (and warns, and wraps). Asking it here, against a value that does not exist yet, can only ever
 * give the wrong answer.
 */
function isRotationSetupIncomplete(d: RotationBlockEditorData): boolean {
  const set = (v: string) => v.trim() !== '';
  const inDomain = (v: string, lo: number, hi: number) =>
    hasVariableChip(v) || (num(v) >= lo && num(v) <= hi);
  if (d.conditionType === 'exact') {
    return !d.answers.some(a => set(a.value) && inDomain(a.value, ROTATION_MIN, ROTATION_MAX));
  }
  if (d.conditionType === 'between') {
    return !d.betweenAnswers.some(a => set(a.low) && set(a.high)
      && inDomain(a.high, ROTATION_MIN, Infinity) && inDomain(a.low, -Infinity, ROTATION_MAX));
  }
  if (!set(d.threshold)) return true;
  return !inDomain(d.threshold, ROTATION_MIN, ROTATION_MAX);
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical aria-hidden='true' className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI (targeted/targeted feedback) ─────────────────────────────────

function RotationTriggerEditor({ trigger, onChange, t, variables, rolledValues, onPlainFieldFocus, fieldId }: {
  /** The angle-mode key for this trigger's fields. A range's two bounds share it, because two
   *  bounds of one measurement disagreeing about degrees is never what anyone meant. */
  fieldId: string;
  /** Releases the shared rich-text toolbar — see FormulaField's `onFocus`. */
  onPlainFieldFocus: () => void;
  trigger: string; onChange: (t: string) => void; t: (k: string, opts?: Record<string, unknown>) => string;
  variables: TaskVariable[]; rolledValues: Record<string, string>;
}) {
  const { type: trigType, v1, v2 } = parseRotationTrigger(trigger);
  const loNum = parseFloat(v1), hiNum = parseFloat(v2);
  const rangeInverted = trigType === 'range' && v1 !== '' && v2 !== ''
    && Number.isFinite(loNum) && Number.isFinite(hiNum) && loNum > hiNum;

  return (
    <div className='flex flex-col gap-2'>
      <div role='group' aria-label={t('blocks.conditions.typeLabel')} className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
        {TRIGGER_TYPES.map(({ id, symbol }) => (
          <button
            key={id}
            type='button'
            aria-pressed={trigType === id}
            aria-label={t(`blocks.rotation.${TRIGGER_ARIA_KEYS[id]}`)}
            onClick={() => onChange(buildRotationTrigger(id, v1, v2))}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
              trigType === id ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
            }`}
          >
            {symbol}
          </button>
        ))}
      </div>
      {trigType === 'range' ? (
        <>
          {/* STACKED, matching slider's own trigger pair: side by side, each bound lost roughly
              half its width to the variables picker and the degree suffix, and a bound can now hold
              a chip-rendered formula that does not fit what was left. */}
          <div className='flex flex-col gap-1.5'>
            <FormulaField
              fieldId={`${fieldId}.low`} angleModeKey={fieldId}
              hideEval
              value={v1}
              onChange={v => onChange(buildRotationTrigger('range', v, v2))}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.rotation.triggerRangeMinAria')}
              placeholder={t('blocks.triggers.triggerRangeMin')}
              suffix='°'
              hasError={rangeInverted}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
            <span aria-hidden='true' className='self-center text-xs text-ws-muted'>{t('blocks.connectors.to')}</span>
            <FormulaField
              fieldId={`${fieldId}.high`} angleModeKey={fieldId}
              hideEval
              value={v2}
              onChange={v => onChange(buildRotationTrigger('range', v1, v))}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.rotation.triggerRangeMaxAria')}
              placeholder={t('blocks.triggers.triggerRangeMax')}
              suffix='°'
              hasError={rangeInverted}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
          </div>
        </>
      ) : (
        <FormulaField
          fieldId={fieldId}
          hideEval
          value={v1}
          onChange={v => onChange(buildRotationTrigger(trigType, v, ''))}
          variables={variables}
          rolledValues={rolledValues}
          ariaLabel={t('blocks.rotation.triggerValuePlaceholder')}
          /* No placeholder. A greyed "Angle (°)" in an empty condition field reads as a value
                that is already there, which is the same confusion the invented `lt:10` seed caused
                one line up. The field's aria-label still names it for a screen reader; sighted
                users have the condition's own symbol immediately to its left. */
          suffix='°'
          className='w-full' 
          onFocus={onPlainFieldFocus}/>
      )}
    </div>
  );
}

function RotationTriggerChip({ trigger, evaluation, t, scenario, angleMode }: {
  /** THIS trigger's own angle mode — it is a formula field like any other. The caller looks it
   *  up with `angleModeFor`, because only the block knows the map. */
  angleMode: 'degrees' | 'radians';

  trigger: string; evaluation: FeedbackState['evaluation']; t: (k: string, opts?: Record<string, unknown>) => string; scenario: Record<string, string>;
}) {
  const chipColor =
    chipToneClass(evaluation);
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      <span className={TRIGGER_CHIP_TEXT_CLASS}>{resolvedTriggerChip({
        trigger, scenario,
        summaryOf: resolved => rotationTriggerSummary(resolved, t),
        evaluate: (expr, values) => evaluateFormulaRaw(expr, values, angleMode),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      })}</span>
    </span>
  );
}

// ─── Correct-answer condition UI (SegmentedControl + exact/between/threshold) ─────────────
// Ported from slider-block.tsx's SegmentedControl + AnswerSetupFields, unchanged in behavior
// except the fixed [ROTATION_MIN, ROTATION_MAX] domain in place of a teacher-configurable min/max.

interface AnswerSetupFieldsProps {
  t: (k: string, opts?: Record<string, unknown>) => string;
  conditionType: RotationConditionType;
  onConditionTypeChange: (v: RotationConditionType) => void;
  answers: ExactAnswer[];
  onUpdateAnswer: (id: string, patch: Partial<ExactAnswer>) => void;
  onAddAnswer: () => void;
  onRemoveAnswer: (id: string) => void;
  /** Flips ONE answer between a single tolerance and a pair — the setting is per answer. */
  onToggleAnswerSymmetry: (id: string) => void;
  betweenAnswers: BetweenAnswer[];
  onUpdateBetweenAnswer: (id: string, patch: Partial<BetweenAnswer>) => void;
  onAddBetweenAnswer: () => void;
  onRemoveBetweenAnswer: (id: string) => void;
  threshold: string;
  onThresholdChange: (v: string) => void;
  armedAnswerId: string | null;
  setArmedAnswerId: (id: string | null) => void;
  armedBetweenId: string | null;
  setArmedBetweenId: (id: string | null) => void;
  /** Clears the shared rich-text toolbar's active-editor reference. Every plain <input> in this
   * block calls it on focus: without it the toolbar stays pointed at whichever RichTextEditor was
   * last active (the Question), so hitting Bold while the cursor sits in a numeric field silently
   * formats the question instead. Same fix MCQ/Ordering's own alt-text and label fields carry. */
  onPlainFieldFocus: () => void;
  /** Task variables offered by the answer fields' chip picker. Answer VALUES accept formulas;
   * tolerance deliberately does not — see the block's own note on why a tolerance on a loop is
   * absolute degrees only. */
  variables: TaskVariable[];
  /** The roll the answer fields preview against — `{}` in plain display, where chips stay
   * unresolved. */
  rolledValues: Record<string, string>;
}

function AnswerSetupFields({
  t, conditionType, onConditionTypeChange, answers, onUpdateAnswer, onAddAnswer, onRemoveAnswer,
  onToggleAnswerSymmetry,
  betweenAnswers, onUpdateBetweenAnswer, onAddBetweenAnswer, onRemoveBetweenAnswer,
  threshold, onThresholdChange, armedAnswerId, setArmedAnswerId, armedBetweenId, setArmedBetweenId, onPlainFieldFocus,
  variables, rolledValues,
}: AnswerSetupFieldsProps) {
  const addAnswerBtnRef = useRef<HTMLButtonElement>(null);
  const addBetweenBtnRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      <SegmentedControl fieldId='conditionType'
        options={CONDITION_TYPES.map(c => ({ id: c.id, label: c.symbol, ariaLabel: t(`blocks.conditions.${CONDITION_ARIA_KEYS[c.id]}`) }))}
        value={conditionType}
        onChange={v => onConditionTypeChange(v as RotationConditionType)}
        aria-label={t('blocks.conditions.typeLabel')}
        stretch
      />

      {conditionType === 'exact' && (
        <div className='flex flex-col gap-3'>
          {answers.map((a, i) => {
            const isAsym = answerSymmetry(a) === 'asymmetric';
            const isArmed = armedAnswerId === a.id;
            const aOutOfRange = a.value.trim() !== '' && (num(a.value) < ROTATION_MIN || num(a.value) > ROTATION_MAX);
            // The tolerance fields carry the same treatment Compass's do. Reporting the cap only in
            // the issue bar left the offending box looking fine, so with several answers (and two
            // halves each when asymmetric) a teacher had to guess which one was at fault.
            const tolOver = (v: string) => v.trim() !== '' && num(v) > ROTATION_MAX_TOLERANCE;
            const belowOver = tolOver(a.toleranceBelow), aboveOver = tolOver(a.toleranceAbove), symOver = tolOver(a.toleranceValue);
            const toleranceWarningId = `angle-tol-warn-${a.id}`;
            const anyTolOver = belowOver || aboveOver || symOver;
            return (
              <div key={a.id} data-field-id={a.id} className={`flex flex-col gap-1 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''}`}>
              <div className='flex items-center gap-2'>
                {/* No filterPositiveNumber: it strips everything but digits, which is right for a
                    tolerance and wrong for a formula. FormulaField owns its own input discipline. */}
                <FormulaField fieldId={`${a.id}.value`}
                  hideEval
                  value={a.value}
                  onChange={v => onUpdateAnswer(a.id, { value: v })}
                  variables={variables}
                  rolledValues={rolledValues}
                  ariaLabel={answers.length > 1 ? `${t('blocks.answers.fieldValue')} ${i + 1}` : t('blocks.answers.fieldValue')}
                  hasError={aOutOfRange}
                  className='min-w-0 flex-1'
                  onFocus={onPlainFieldFocus}/>
              </div>
                {/* The tolerance has its own row in both modes, and `decimal` not `scientific`
                    because a rotation runs 0..360 — see `ToleranceRow` for both calls. This block
                    was the last on the old layout, with `±` outside a fixed box on the answer's
                    own row: the reason the shared component exists. */}
                <ToleranceRow
                  idPrefix={a.id}
                  asymmetric={isAsym}
                  grammar='decimal'
                  toleranceValue={a.toleranceValue}
                  toleranceBelow={a.toleranceBelow}
                  toleranceAbove={a.toleranceAbove}
                  onChange={patch => onUpdateAnswer(a.id, patch)}
                  warn={{ value: symOver, below: belowOver, above: aboveOver }}
                  describedBy={toleranceWarningId}
                  onFocus={onPlainFieldFocus}
                  trailing={<>
                    <SymmetryToggle compact fieldId={`${a.id}.toleranceSymmetry`}
                      asymmetric={isAsym} onToggle={() => onToggleAnswerSymmetry(a.id)} />
                    {answers.length > 1 && (
                      <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                        aria-label={isArmed ? t('blocks.answers.confirmRemoveAnswer') : t('blocks.answers.removeAnswer')}
                        className='shrink-0'
                        onClick={() => {
                          if (isArmed) { onRemoveAnswer(a.id); setArmedAnswerId(null); requestAnimationFrame(() => addAnswerBtnRef.current?.focus()); }
                          else { setArmedBetweenId(null); setArmedAnswerId(a.id); }
                        }}
                        onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedAnswerId(null); }}
                        onBlur={() => { if (isArmed) setArmedAnswerId(null); }}
                      >
                        <Trash2 className='h-4 w-4' />
                      </IconButton>
                    )}
                  </>} />
              {/* sr-only, not a visible row: this editor states a block's problems in the issue bar,
                  and the amber border already carries it visually. The text exists so
                  `aria-describedby` gives a screen-reader user what the border gives a sighted one. */}
              {anyTolOver && (
                <p id={toleranceWarningId} className='sr-only'>
                  {t('blocks.rotation.validationToleranceMax', { max: ROTATION_MAX_TOLERANCE })}
                </p>
              )}
              </div>
            );
          })}

          {answers.length < 5 && (
            <AddRowButton ref={addAnswerBtnRef} onClick={onAddAnswer} label={t('blocks.answers.addAnswer')} />
          )}
        </div>
      )}

      {conditionType === 'between' && (
        <div className='flex flex-col gap-3'>
          {betweenAnswers.map((a, i) => {
            const isArmed = armedBetweenId === a.id;
            const bothBoundsSet = a.low.trim() !== '' && a.high.trim() !== '';
            const rangeOutOfRange = bothBoundsSet && (num(a.high) < ROTATION_MIN || num(a.low) > ROTATION_MAX);
            return (
              // `data-field-id` so `between-incomplete` can reach this row. Without it the issue
              // renders as a BUTTON (any non-empty `fields` does) that silently does nothing — the
              // dead control `IssueRow` documents avoiding, and the fault numerical's unit options
              // shipped with.
              <BetweenAnswerRow
                key={a.id}
                fieldId={a.id}
                armed={isArmed}
                grouped={betweenAnswers.length > 1}
                to={t('blocks.connectors.to')}
                low={
                  <FormulaField fieldId={`${a.id}.low`} angleModeKey={a.id}
                    hideEval
                    value={a.low}
                    onChange={v => onUpdateBetweenAnswer(a.id, { low: v })}
                    variables={variables}
                    rolledValues={rolledValues}
                    ariaLabel={betweenAnswers.length > 1 ? `${t('blocks.triggers.triggerRangeMin')} ${i + 1}` : t('blocks.triggers.triggerRangeMin')}
                    placeholder={t('blocks.triggers.triggerRangeMin')}
                    hasError={rangeOutOfRange}
                    className='min-w-0 flex-1' 
                    onFocus={onPlainFieldFocus}/>
                }
                high={
                  <FormulaField fieldId={`${a.id}.high`} angleModeKey={a.id}
                    hideEval
                    value={a.high}
                    onChange={v => onUpdateBetweenAnswer(a.id, { high: v })}
                    variables={variables}
                    rolledValues={rolledValues}
                    ariaLabel={betweenAnswers.length > 1 ? `${t('blocks.triggers.triggerRangeMax')} ${i + 1}` : t('blocks.triggers.triggerRangeMax')}
                    placeholder={t('blocks.triggers.triggerRangeMax')}
                    hasError={rangeOutOfRange}
                    className='min-w-0 flex-1' 
                    onFocus={onPlainFieldFocus}/>
                }
                action={betweenAnswers.length > 1 && (
                  <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                    aria-label={isArmed ? t('blocks.answers.confirmRemoveRange') : t('blocks.answers.removeRange')}
                    className='shrink-0'
                    onClick={() => {
                      if (isArmed) { onRemoveBetweenAnswer(a.id); setArmedBetweenId(null); requestAnimationFrame(() => addBetweenBtnRef.current?.focus()); }
                      else { setArmedAnswerId(null); setArmedBetweenId(a.id); }
                    }}
                    onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedBetweenId(null); }}
                    onBlur={() => { if (isArmed) setArmedBetweenId(null); }}
                  >
                    <Trash2 className='h-4 w-4' />
                  </IconButton>
                )}
              />
            );
          })}
          {betweenAnswers.length < 5 && (
            <AddRowButton ref={addBetweenBtnRef} onClick={onAddBetweenAnswer} label={t('blocks.answers.addAnswer')} />
          )}
        </div>
      )}

      {ONE_SIDED.includes(conditionType) && (() => {
        const thresholdOutOfRange = threshold.trim() !== '' && (num(threshold) < ROTATION_MIN || num(threshold) > ROTATION_MAX);
        return (
          <div className='flex flex-col gap-1'>
            <FormulaField fieldId='threshold'
              hideEval
              value={threshold}
              onChange={v => onThresholdChange(v)}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.answers.fieldValue')}
              placeholder={t('blocks.answers.fieldValue')}
              hasError={thresholdOutOfRange}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
          </div>
        );
      })()}
    </>
  );
}

// ─── Collapsed-view display ────────────────────────────────────────────────────

// The editor-local string shape converted into the parsed-number config rotation.ts' interval math
// expects — same purpose as compass-block.tsx's own compassAcceptedRangeConfig.
/** `resolve` turns the editor's raw formula text ("{n}*30") into the number the maths needs.
 * Defaults to `num`, so every existing caller — and any plain-number setup — behaves exactly as
 * before. Tolerances deliberately never resolve: a tolerance on a loop is absolute degrees, not an
 * equation. Same bridge shape slider and numerical already use. */
/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists several. Shared by both non-editing render paths (the collapsed
 * card's test widget and the student-facing Preview). */
function buildNormalizedRotationData(
  rawD: RotationBlockEditorData,
  t: (key: string, opts?: Record<string, unknown>) => string,
): RotationBlockEditorData {
  return {
    ...rawD,
    feedback: normalizeStandardFeedback(rawD.feedback, t, rawD.gradingMode, {
      defaults: { unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>` },
      // Angle's first sensor state. It had none — the only sensor block that could fail with
      // nothing to say about it, and so the only one absent from the Flow rungs entirely.
      extraDefaults: ROTATION_SENSOR_DEFAULTS(t),
    }),
  };
}

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `rotationIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function rotationBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return rotationIssues(buildNormalizedRotationData(initData(block.data), ctx.t), ctx);
}

export function rotationIssues(d: RotationBlockEditorData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryFeedback = t('blocks.areas.feedback');

  // Three kinds of field, and `num()` cannot tell them apart: it returns 0 for anything unparseable,
  // so before this every FORMULA answer validated as though it were a literal 0. Same class of bug
  // `hasNum()` exists for. Each check asks what KIND of field it has before anything else.
  // The threshold is included: under lt/lte/gt/gte it IS the answer.
  /** Each answer field's text WITH its id, so a check can read that field's own angle mode. */
  const answerFieldRows: { value: string; fieldId: string }[] =
    d.conditionType === 'exact' ? d.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
    : d.conditionType === 'between'
      ? d.betweenAnswers.flatMap(a => [
        { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }])
      : [{ value: d.threshold, fieldId: 'threshold' }];
  const answerFields: string[] = answerFieldRows.map(r => r.value);
  const probeValues = probeRoll(variables);
  const isFormula = (v: string) => hasVariableChip(v);
  const anyFormulaAnswer = answerFields.some(isFormula);

  const outOfDomain = (v: string) => v.trim() !== '' && !isFormula(v) && !(num(v) >= ROTATION_MIN && num(v) <= ROTATION_MAX);
  if (answerFields.some(outOfDomain)) {
    issues.push({ key: 'answer-range', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.rotation.validationValueOutOfRange') });
  }

  // Judged against a PROBE roll from the variables' own domains, never a live roll: an unselected
  // card has no roll, so every formula would read NaN and be called broken for being looked at from
  // the wrong surface.
  if (answerFieldRows.some(r => r.value.trim() !== '' && isFormula(r.value)
    && !Number.isFinite(evaluateFormulaRaw(r.value, probeValues, angleModeFor(d, r.fieldId))))) {
    issues.push({ key: 'answer-formula-invalid', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.answers.issueAnswerFormulaInvalid') });
  }

  // A resolved target outside [0, 360) WRAPS — 390 is 30 — so this warns rather than errors.
  // Quantified over the extrema: judged on one roll it would appear and vanish as the dice turn.
  if (anyFormulaAnswer) {
    const extrema = computeFormulaExtrema({
      conditionType: d.conditionType, answers: d.answers, betweenAnswers: d.betweenAnswers,
      threshold: d.threshold, toleranceMode: 'absolute', toleranceSymmetry: d.toleranceSymmetry, angleMode: 'degrees',
    }, variables);
    // NOT "outside [0, 360)". A band dipping below zero is the ordinary wraparound case on a
    // circular domain — this block's own note for range triggers says so outright — and −10 is a
    // perfectly natural way to write "just under level". Warning about it flags the most readable
    // way to express a band straddling zero as a defect.
    //
    // Two things are genuinely worth saying. Beyond a FULL TURN is more than one rotation and is
    // nearly always a coefficient slip. A band spanning 360 or more accepts every possible reading,
    // so the block grades nothing while looking configured — slider reports the same shape as
    // `tolerance-covers-track`.
    if (extrema && (extrema.lo <= -360 || extrema.hi >= 720)) {
      issues.push({ key: 'answer-beyond-turn', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
        detail: t('blocks.rotation.issueAnswerBeyondTurn') });
    }
    /**
     * A band that spans the whole circle for SOME student — measured as the band's own WIDTH at each
     * roll, not as the envelope of where it can sit across rolls.
     *
     * The envelope was the bug. `computeFormulaExtrema` answers "how far can this reach", so an
     * answer of `{n}` over 1..400 reported a 399° span and tripped this — while every individual
     * student got a single heading with no tolerance at all. The block was graded perfectly well;
     * only the check thought otherwise.
     *
     * Restricted to `between`, because that is the only shape whose width can vary with the roll.
     * An exact answer's width is twice its tolerance, which does not depend on any variable and is
     * already reported, more precisely, by `tolerance-max`.
     */
    const bandWidth = (vals: Record<string, string>) => d.betweenAnswers.some(a => {
      const lo = evaluateFormulaRaw(a.low, vals, angleModeFor(d, a.id));
      const hi = evaluateFormulaRaw(a.high, vals, angleModeFor(d, a.id));
      return Number.isFinite(lo) && Number.isFinite(hi) && Math.abs(hi - lo) >= 360 - 1e-9;
    });
    if (d.conditionType === 'between') {
      const bandNames = chipNamesIn(d.betweenAnswers.flatMap(a => [a.low, a.high]));
      // ANY roll, not every: a band that swallows the circle for one student in ten is still a
      // student whose answer cannot be wrong, and the teacher cannot see that from their own screen.
      let coversCircle = false;
      if (bandNames.length === 0) {
        coversCircle = bandWidth(probeValues);
      } else {
        forEachRoll(bandNames, variables, vals => {
          if (bandWidth(vals)) { coversCircle = true; return 'stop'; }
          return undefined;
        }, OVERLAP_ENUMERATION_MAX_COMBOS);
      }
      if (coversCircle) {
        issues.push({ key: 'answer-covers-circle', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
          detail: t('blocks.rotation.issueAnswerCoversCircle') });
      }
    }
  }

  // Only `exact` carries a tolerance; a `between` answer states its own two edges, and a threshold
  // condition is an open half-line with no tolerance at all. Matches Compass's own tolerance-max.
  const toleranceTooLarge = d.conditionType === 'exact' && d.answers.some(a => {
    const vals = answerSymmetry(a, d) === 'asymmetric' ? [a.toleranceAbove, a.toleranceBelow] : [a.toleranceValue];
    return vals.some(v => v.trim() !== '' && num(v) > ROTATION_MAX_TOLERANCE);
  });
  if (toleranceTooLarge) {
    issues.push({ key: 'tolerance-max', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.rotation.validationToleranceMax', { max: ROTATION_MAX_TOLERANCE }) });
  }

  /**
   * Two accepted answers covering the same angles.
   *
   * A warning, not an error: acceptance is their union, so the grading is right either way. What is
   * wrong is the authoring — two rows that look different mean one thing.
   *
   * The seam case is the reason this exists. On a closed circle "0 +/- 2" and "360 +/- 2" are the
   * same answer written two ways, and a teacher has no way to see that from the numbers. The check
   * runs on the wrapped intervals so it catches that without a special case, and the copy names the
   * cause only when a band actually crosses the seam — on a plain 10/12 collision that sentence would
   * be noise.
   */
  // Skipped while a formula answer is present: the comparison needs concrete numbers, and with
  // `num()` turning every formula into 0 it reported two unrelated equations as one answer.
  const overlappingAnswers = anyFormulaAnswer ? [] : findRotationOverlappingAnswerPairs(rotationAcceptedRangeConfig(d));
  if (overlappingAnswers.length > 0) {
    const bandCrossesSeam = d.conditionType === 'exact' && d.answers.some(a => {
      const asym = answerSymmetry(a, d) === 'asymmetric';
      const loErr = num(asym ? a.toleranceBelow : a.toleranceValue);
      const hiErr = num(asym ? a.toleranceAbove : a.toleranceValue);
      return num(a.value) - loErr < ROTATION_MIN || num(a.value) + hiErr > ROTATION_MAX;
    });
    issues.push({
      key: 'answer-overlap',
      severity: 'warning',
      kind: 'defect',
      category: categoryAnswerSetup,
      detail: t(bandCrossesSeam ? 'blocks.rotation.answerOverlapSeamWarning' : 'blocks.rotation.answerOverlapWarning'),
    });
  }

  /*
   * Quantified. This read `num(a.low) > num(a.high)`, and `num()` returns 0 for a formula — so for
   * equation bounds the test was `0 > 0` and the check could not fire even in principle. It worked
   * only for literals, which are the case least likely to be authored backwards by accident.
   */
  // A `between` row with exactly one bound filled is DROPPED by the evaluator, so it grades as
  // though it were not there. This block gated only on "no row is complete", which one complete row
  // satisfies — so a half-filled row beside a complete one was silent. See `incompleteBetweenRowIds`.
  if (d.conditionType === 'between') {
    const halfRows = incompleteBetweenRowIds(d.betweenAnswers);
    if (halfRows.length > 0) {
      issues.push({ key: 'between-incomplete', fields: halfRows, severity: 'error', kind: 'incompletion',
        category: categoryAnswerSetup, detail: t('blocks.answers.issueBetweenIncomplete') });
    }
  }

  const rotationVerdict = d.conditionType === 'between'
    ? betweenInversionVerdict(d.betweenAnswers, variables, 'degrees')
    : 'never';
  if (rotationVerdict === 'always') {
    issues.push({ key: 'answer-range-inverted', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.conditions.betweenInverted') });
  } else if (rotationVerdict === 'sometimes') {
    issues.push({ key: 'answer-range-may-invert', severity: 'warning', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.conditions.betweenMayInvert') });
  } else if (rotationVerdict === 'unknown') {
    issues.push({ key: 'answers-unverifiable', severity: 'warning', kind: 'unverified', category: categoryAnswerSetup, detail: t('blocks.answers.issueTooWideToVerify') });
  }


  if (!d.question.replace(/<\/?p>/g, '').trim()) {
    issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  }

  // A variable chip in the QUESTION with a fixed answer is silent mis-grading: every student reads a
  // different target and every student is graded against the same one. No sensor block could detect
  // this — `htmlHasVariableChips` was called zero times in all three. A defect, not an incompletion:
  // both halves are authored, and more typing in the question will not resolve it.
  //
  // The "answer can vary" side is written against the mechanism each block actually has, so this
  // stays correct as those mechanisms land: chips in an answer field for compass/angle, a configured
  // branch set for gps.
  // The `&& !answerOutOfRange` guard is gone: `supersededBy` is the one mechanism for standing a
  // catch-all down, and doing it twice meant every new key had to be added in two places.
  // Graded only: optional and survey author no answer key, so a missing one is the mode
  // working as intended rather than a setup the teacher has not finished.
  if (d.gradingMode === 'graded' && isRotationSetupIncomplete(d)) {
    issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion', supersededBy: ['answer-range', 'answer-formula-invalid', 'answer-range-inverted', 'answer-overlap', 'tolerance-max'], category: categoryAnswerSetup, detail: t('blocks.rotation.barSetupIncomplete') });
  }

  const targetedStates = d.feedback.states.filter(s => s.type === 'targeted');
  const triggers = targetedStates.map(s => s.trigger);
  /** A trigger's own field, so the overlap analysis reads it in the mode it was authored in.
   *  Without this every trigger resolved in the block default and a radian trigger looked
   *  identical to a degree answer — reported as "completely overlaps Correct". */
  const triggerFieldId = new Map(targetedStates.map(s => [s.trigger, `feedback.${s.id}.trigger`] as const));
  if (triggers.some(trig => {
    const { v1, v2 } = parseRotationTrigger(trig);
    return outOfDomain(v1) || outOfDomain(v2);
  })) {
    issues.push({ key: 'trigger-range', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.rotation.validationValueOutOfRange') });
  }
  if (triggers.some(trig => {
    const { type, v1, v2 } = parseRotationTrigger(trig);
    return type === 'range' && v1 !== '' && v2 !== '' && Number.isFinite(num(v1)) && Number.isFinite(num(v2)) && num(v1) > num(v2);
  })) {
    issues.push({ key: 'trigger-range-inverted', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.conditions.betweenInverted') });
  }
  /*
   * The FORMULA case, which the literal check above cannot see: `num()` returns 0 for an equation,
   * so `{a} > {b}` is `0 > 0` and never fires. Same blindness its answer check had.
   *
   * A WARNING, not an error, even for `always`: an inverted feedback band means that state never
   * fires and the student falls through to Default Incorrect — still graded, just less specific —
   * whereas an inverted ANSWER band means nobody can be right. The blocks' reachability machinery
   * is what says "this state cannot fire".
   */
  const trigVerdict = triggerRangeInversionVerdict(triggers, variables, 'degrees');
  if (trigVerdict === 'always' || trigVerdict === 'sometimes') {
    issues.push({ key: 'trigger-range-may-invert', severity: 'warning', kind: 'defect', category: categoryFeedback,
      detail: t('blocks.conditions.betweenMayInvert') });
  }


  /**
   * Every fault the shared detector finds in a targeted condition.
   *
   * `allowWrap` is the one thing that differs between the blocks that use this, and it is not a
   * matter of taste. Angle is the opposite of compass here, deliberately: `RotationBlockData` states that a
   * single low/high pair is always taken literally with no wraparound inferred from ordering, and a
   * band that should straddle the seam is authored as two answers. So an inverted range IS an error,
   * which is why this block already reported one before the shared detector arrived.
   */
  const triggerFaults = d.feedback.states
    .filter(s => s.type === 'targeted')
    .map(s => triggerFault(s.trigger, {
      // The same resolver the answer fields are judged with, a few lines above, in THIS
      // trigger's own angle mode.
      resolve: (v: string) => evaluateFormulaRaw(v, probeValues, angleModeFor(d, `feedback.${s.id}.trigger`)),
      declared: new Set(variables.map(v => v.name)),
      probeable: new Set(Object.keys(probeValues)),
      allowWrap: false,
    }))
    .filter((f): f is TriggerFault => f !== null);

  if (triggerFaults.includes('unresolvable')) {
    issues.push({ key: 'trigger-unresolvable', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.conditions.issueUnresolvable') });
  }
  if (triggerFaults.includes('no-value')) {
    issues.push({ key: 'trigger-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.conditions.issueNoValue') });
  }
  if (triggerFaults.includes('range-incomplete')) {
    issues.push({ key: 'trigger-range-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.conditions.issueRangeIncomplete') });
  }

  if (hasEmptyFeedbackContent(d.feedback.states)) {
    issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });
  }

  if (d.gradingMode === 'graded' && !isRotationSetupIncomplete(d)) {
    if (findRotationOverlappingFeedbackPairs(triggers).length > 0) {
      issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.rotation.feedbackOverlapWarning') });
    }
    /**
     * Quantified over the roll space, not judged at one point of it.
     *
     * Two things were wrong with the single check this replaces. It resolved the answer with `num`,
     * which returns 0 for anything unparseable — so a formula answer put the accepted band around
     * ZERO and every claim below was made against a band the block does not have. And even with a
     * resolver it would have been one roll's answer about a band that moves with the dice.
     */
    const overlap = analyseOverlapAcrossRolls({
      triggers, variables,
      names: chipNamesIn([...answerFields, ...triggers]),
      atRoll: vals => {
        const resolveAt = (v: string, fieldId = '') => evaluateFormulaRaw(v, vals, angleModeFor(d, fieldId));
        const ranges = computeRotationAcceptedRanges(rotationAcceptedRangeConfig(d, resolveAt));
        if (ranges.length === 0) return null;
        const at = (trig: string) =>
          resolveTriggerValues(trig, v => resolveAt(v, triggerFieldId.get(trig) ?? ''));
        return {
          subsumed: trig => isRotationFeedbackFullySubsumed(at(trig), ranges),
          overlaps: trig => doesRotationFeedbackOverlapAccepted(at(trig), ranges),
        };
      },
    });
    // Graded only. Every verdict here is about the ACCEPTED BAND, and in a participation mode
    // there is no band in force — correct is never checked, so nothing is subsumed by it and
    // nothing overlaps it. Reporting these outside graded would warn about a condition the
    // evaluator will happily fire.
    if (d.gradingMode === 'graded' && overlap.any.alwaysSubsumed) {
      issues.push({ key: 'feedback-subsumed', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.rotation.feedbackSubsumedWarning') });
    }
    if (d.gradingMode === 'graded' && overlap.any.alwaysOverlaps) {
      issues.push({ key: 'feedback-overlaps-correct', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.rotation.feedbackOverlapsCorrectWarning') });
    }
    // The collision a teacher cannot see, because the roll in front of them does not contain it.
    if (d.gradingMode === 'graded' && overlap.any.mayCollide) {
      issues.push({ key: 'feedback-may-overlap-correct', severity: 'warning', kind: 'defect',
        supersededBy: ['feedback-overlaps-correct', 'feedback-subsumed'],
        category: categoryFeedback, detail: t('blocks.conditions.mayOverlapCorrect') });
    }
  }

  return issues;
}

const rotationIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();

// ─── Live test: scenario-driven simulated readings ─────────────────────────────
// See compass-block.tsx's own header comment for why this is scenario-driven rather than a
// free-typed field. Angle needs it most of the three: a teacher can walk to a location and can
// physically turn to face a bearing, but cannot hold a laptop at an arbitrary inclination while
// authoring, so without simulation this block is effectively untestable at authoring time.
//
// The four gesture-failure scenarios model how a tilt reading actually goes wrong in the field.
// Their exact numeric mapping is a modelling choice, documented per-case below — worth correcting
// against real device behaviour if any of them turns out to read differently in practice.
/**
 * Simulated SENSOR FAULTS, and nothing else.
 *
 * The value scenarios this list used to carry are gone. An angle is a single number the teacher can
 * type into the box beside this dropdown, so a preset whose only contribution is a typeable number
 * earns nothing — and several of the old ones had begun to lie: `flatOnTable` was a literal 0,
 * `upsideDown` returned the supplement that only the pre-tracker `acos` reading would have produced,
 * and `axisAmbiguity` returned the complementary angle for a failure the derived pivot now detects.
 *
 * What survives is what the box cannot express, and it maps onto the three real states the tracker
 * can be in when something has gone wrong. GPS keeps its full preset list for the opposite reason: a
 * point 10 m off target is a coordinate pair, not something anyone types.
 */
type RotationScenarioId =
  /** No capture at all. The only route to No Answer, since Live Reading always has a value and
   * Capture on Submit takes one automatically. */
  | 'noReading'
  /** The sample stream broke mid-gesture, so the turn count is gone and the tracker refuses to
   * report. Drops the calibration exactly as the real interruption does. */
  | 'trackingLost'
  /** The phone twisted off the plane it started in. A reading still exists; it has just started to
   * foreshorten, and the off-axis check is what notices. */
  | 'offPlane';

/** The authored answer reduced to a centre angle plus a one-sided tolerance. First configured
 * answer only — a simulated reading is a single number. */
/**
 * A centre + tolerance for EVERY authored answer, ported from Compass's compassSimBases.
 *
 * This used to `.find()` the first non-blank answer and simulate only that one, so on a multi-answer
 * block the others were never exercised by any amount of rerolling — and a scenario named for being
 * wrong could land on one of them and report Correct.
 *
 * Threshold conditions still yield exactly one entry: they accept an open half-line, so "the centre"
 * is the threshold itself and there is no symmetric tolerance — scenarios step off the boundary.
 */
function rotationSimBases(d: RotationBlockEditorData): { center: number; tolerance: number }[] {
  if (isRotationSetupIncomplete(d)) return [];
  if (d.conditionType === 'exact') {
    return d.answers.filter(a => a.value.trim() !== '').map(a => {
      const asym = answerSymmetry(a, d) === 'asymmetric';
      const above = asym ? num(a.toleranceAbove) : num(a.toleranceValue);
      const below = asym ? num(a.toleranceBelow) : num(a.toleranceValue);
      return { center: num(a.value), tolerance: Math.max(above, below, 0) };
    });
  }
  if (d.conditionType === 'between') {
    return d.betweenAnswers.filter(a => a.low.trim() !== '' && a.high.trim() !== '').map(a => {
      const lo = num(a.low), hi = num(a.high);
      return { center: (lo + hi) / 2, tolerance: Math.abs(hi - lo) / 2 };
    });
  }
  const v = num(d.threshold);
  return Number.isFinite(v) ? [{ center: v, tolerance: 0 }] : [];
}

/** The first authored answer, for callers that only need "is anything set up at all". */
function rotationSimBase(d: RotationBlockEditorData): { center: number; tolerance: number } | null {
  return rotationSimBases(d)[0] ?? null;
}

/** Clamped, not wrapped: Angle's domain is the closed [0, 360] loop with no seam-crossing
 * concept (see rotation.ts), so a scenario that would overshoot the end sits at the end. */
function clampRotation(v: number): number {
  return Math.min(ROTATION_MAX, Math.max(ROTATION_MIN, v));
}

/** Every fault is always offered: none of them is derived from the answer setup, so none can become
 * unrepresentable the way the old value scenarios could. */
function rotationScenarioIds(): RotationScenarioId[] {
  return ['noReading', 'trackingLost', 'offPlane'];
}

/** Whether an armed fault leaves anything for the manual box to hold. */
function rotationFaultSuppressesValue(id: RotationScenarioId): boolean {
  return id === 'noReading' || id === 'trackingLost';
}

/**
 * The simulated reading for an armed fault.
 *
 * Only two outcomes now: nothing at all, or the value the teacher typed (falling back to an authored
 * answer so the faults stay usable before anything has been typed). The randomised, answer-derived
 * draws are gone with the value scenarios, and with them the reason this needed a nonce.
 */
function rotationSimReading(id: RotationScenarioId, d: RotationBlockEditorData, manual: number | null): number | null {
  if (rotationFaultSuppressesValue(id)) return null;
  if (manual != null) return clampRotation(manual);
  return rotationSimBases(d)[0]?.center ?? null;
}

interface RotationTestData {
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  hasChips: boolean;
}

/**
 * A band as text, with the whole domain named rather than described.
 *
 * `0° to 360°` is the complement of an answer key that accepts nothing, and printing the bounds
 * makes it look like a configured range a student could miss. Compass has always collapsed the
 * equivalent band to "Any heading" (formatCardinalRange); Angle spelled the numbers out.
 */

/**
 * Fans one feedback state out into one legend pill per range — the same breakdown the feedback panel
 * renders, just as TestBarChips. Follows MCQ's convention for a multi-part condition: each part is its
 * own pill, because a single joined label hides half of what the state actually covers.
 *
 * No connectors between them: a legend chip lights up when its state fires, which already tells the
 * teacher which range was hit, and the rail is far too narrow to spend a row on the word "or".
 */
/**
 * ANGLE'S ONE DERIVATION of what a default state's condition looks like, in either mode.
 *
 * Angle answered this in five places split by mode and surface — `correctFan`/`incorrectFan` for
 * the legend, `summarizeCorrect` for the answer bar, `incorrectFormulaChip` for the panel when
 * unrolled, the per-gap chips when rolled — plus two lib label functions. Compass had the same
 * six-way split and the same consequence. See `compassChips` for the full reasoning; this is its
 * twin, differing only in that an angle has one-sided condition types (`< {n}`) and so carries a
 * comparison symbol on its unrolled reading.
 */

/** `resolve` turns the editor's raw formula text ("{n}*30") into the number the maths needs.
 * Defaults to `num`, so every existing caller — and any plain-number setup — behaves exactly as
 * before. Tolerances deliberately never resolve: a tolerance on a loop is absolute degrees, not an
 * equation. Same bridge shape slider and numerical already use. */
/** Angle's sensor states, in the order `evaluateRotation` gates them. Declared once because the
 *  editor hook and the non-editing render paths both seed them, and a block whose panel and test
 *  legend disagree about what exists is worse than one with neither. */
const ROTATION_SENSOR_DEFAULTS = (t: (key: string, opts?: Record<string, unknown>) => string) => [
  {
    trigger: NO_SENSOR_DATA_TRIGGER,
    title: t('blocks.sensors.noSensorDataFeedback'),
    content: `<p>${t('blocks.rotation.noSensorDataDefault')}</p>`,
    evaluation: 'neutral' as const,
  },
  {
    trigger: 'poor_axis',
    title: t('blocks.rotation.poorAxisFeedback'),
    content: `<p>${t('blocks.rotation.poorAxisDefault')}</p>`,
    evaluation: 'neutral' as const,
  },
];

/** Exported for `angle-mode-grading`, which grades through the REAL chain rather than a
 *  stand-in: the point of that test is that the app's own wiring resolves per field. */
export function rotationBlockDataForEval(d: RotationBlockEditorData, resolve: (s: string, fieldId?: string) => number = num): RotationBlockData {
  return {
    question: d.question,
    conditionType: d.conditionType,
    answers: d.answers.map(a => ({ id: a.id, value: resolve(a.value, `${a.id}.value`), toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow) })),
    betweenAnswers: d.betweenAnswers.map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    toleranceSymmetry: d.toleranceSymmetry,
    threshold: resolve(d.threshold, 'threshold'),
    bypassEnabled: d.bypassEnabled,
    gradingMode: d.gradingMode,
    feedback: d.feedback,
  };
}

/** Whether the ANSWER holds an equation — see the compass twin. The threshold counts: under
 * lt/lte/gt/gte it IS the answer. */
function rotationAnswerHasFormula(d: RotationBlockEditorData): boolean {
  return d.conditionType === 'exact' ? d.answers.some(a => hasVariableChip(a.value))
    : d.conditionType === 'between' ? d.betweenAnswers.some(a => hasVariableChip(a.low) || hasVariableChip(a.high))
    : hasVariableChip(d.threshold);
}

function computeRotationTestData(
  d: RotationBlockEditorData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  reading: number | null,
  hasSubmitted: boolean,
  /** A typed manual-bypass angle, already normalized, or null. Takes precedence over `reading`: a
   * student only reaches for the bypass when the sensor has failed them, so if they have typed an
   * answer that is the answer, whatever the instrument is still claiming. */
  bypassAnswer: number | null = null,
  /** Resolves a formula answer against the roll being tested. Omitted (or `num`) for a caller with
   * no roll in hand, which is exactly right for a plain-number setup. */
  resolve: (s: string, fieldId?: string) => number = num,
  /** The roll the TEST is running against — legend chips resolve, unlike feedback chips. */
  rolledValues: Record<string, string> = {},
  /**
   * The tracker produced nothing — no calibration, no samples, or the simulator's `noReading`.
   *
   * Passed rather than inferred from a null `reading`, because those are different facts: a null
   * reading is also what a student who has not moved the phone yet has, and that one is an
   * ordinary No Answer.
   */
  sensorDead = false,
  /** How far the gesture drifted off its calibration plane, when the tracker measured it. */
  offAxisDegrees: number | null = null,
): RotationTestData {
  const evalData = rotationBlockDataForEval(d, resolve);
  // `bypassAvailable` decides whose fault a dead sensor is, so it is read here and not stored:
  // `unlockBypass` is a Flow effect, and the answer can differ between two attempts.
  const health: SensorHealth = sensorDead
    ? { fault: 'noData', bypassAvailable: d.bypassEnabled }
    : offAxisDegrees != null ? { offAxisDegrees }
    : {};
  // `method` is analytics metadata here rather than a gate — unlike evaluateCompass, evaluateRotation
  // has no accuracy checks to skip, since an Angle reading carries no confidence figure at all.
  const testResult = !hasSubmitted ? null
    : bypassAnswer != null
      // A typed bypass has no instrument behind it, so there is no health to report — the same
      // reason compass passes an unreported heading accuracy for its own bypass.
      // A typed bypass has no instrument behind it and no health to report — the student answered.
      ? evaluateRotation({ touched: true, rotationDegrees: bypassAnswer, method: 'manual' }, {}, evalData)
      : evaluateRotation({ touched: reading != null, rotationDegrees: reading ?? 0, method: 'sensor' }, health, evalData);
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  const states = d.feedback.states;
  const find = (trigger: string) => states.find(s => s.type === 'default' && s.trigger === trigger);

  const chips: TestBarChip[] = [];
  const push = (s: FeedbackState | undefined, category: TestBarChip['category'], condition: React.ReactNode, reachable?: boolean) => {
    if (!s) return;
    chips.push({ id: s.id, condition, evaluation: s.evaluation, active: firedIds.has(s.id), category, reachable });
  };

  /**
   * The participation arms used to emit the catch-all and RETURN, which put the early exit above
   * the targeted loop below — fine while a value block could not fire a targeted state outside
   * graded, wrong the moment it could. The mode now decides what to SKIP, not where to stop.
   */
  const participation = d.gradingMode !== 'graded';

  if (d.gradingMode !== 'optional') {
    push(find('unanswered'), 'unanswered',
      stateChipLabel({ id: '', type: 'rotation', data: d } as never, { type: 'default', trigger: 'unanswered' }, t, locale));
  }

  // A block with no usable answer yet has NO accepted band — not the degenerate one a blank field
  // parses into. The feedback panel has always read it that way: its Correct card is gated on
  // `isRotationSetupIncomplete` and its Default Incorrect gaps come out as the whole domain. The
  // test bar built a band around zero instead, so a freshly added block advertised "0° to 0°" as
  // the correct answer and called the catch-all unreachable — precisely inverting the panel beside
  // it. Same helper on both sides now.
  const setupDead = isRotationSetupIncomplete(d);
  const acceptedRanges = setupDead ? [] : computeRotationAcceptedRanges(rotationAcceptedRangeConfig(d, resolve));
  // Nothing is correct outside graded, so the bar has no Correct chip to draw.
  const correctState = participation ? undefined : find('correct');
  // Hoisted: both default states derive from one function that needs the carve-outs.
  const targetedTriggers = states.filter(s => s.type === 'targeted').map(s => s.trigger);
  /** An EMPTY map means "not rolled yet", not "no variables" — passing it as a roll would resolve
   *  every formula to NaN. */
  const chipRoll = Object.keys(rolledValues).length > 0 ? rolledValues : null;
  /** The angle actually submitted, keyed for `activeIntervalIds` — so the legend lights the ONE
   *  band or gap it fell in rather than every chip of the state. Scope '' because an angle has one
   *  number line, unlike slider's two handles. */
  const submittedByScope: Record<string, number | null> = { '': bypassAnswer ?? reading ?? null };
  const barEnvelope = (st: FeedbackState, category: 'correct' | 'incorrect', list: ConditionChips) => ({
    id: st.id, category, active: firedIds.has(st.id),
    activeSourceIds: activeIntervalIds(list, submittedByScope),
    unreachableLabel: t('blocks.feedbackPanel.unreachable'),
    connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
  });
  // The ONE derivation, in the legend's envelope. The joined-label fallback is gone: it existed
  // only because the per-range fan-out could not describe an unrolled equation, which is the mode
  // fork `rotationChips` now owns. Setup-incomplete stays its own case — there is no answer at all.
  // Derived ONCE and reused for the list and the highlight.
  const correctList = correctState ? rotationChips(d, correctState, chipRoll, t, locale, targetedTriggers) : [];
  const correctFan = (setupDead || !correctState) ? []
    : toTestBarChips(correctList, barEnvelope(correctState, 'correct', correctList));
  if (setupDead) push(correctState, 'correct', t('blocks.feedbackPanel.unreachable'), false);
  else chips.push(...correctFan);

  for (const s of visibleTargeted(states, d.gradingMode)) {
    /*
     * Set Answer holds no value of its own — it IS the authored answer — so running it through
     * the trigger chip printed the operator with its operand missing, "< ?°". Ported from Tilt,
     * which is this block's twin and carries the same note.
     *
     * Rotation is MOTHBALLED and its editor does not offer the preset, so no teacher can author
     * one today. Fixed anyway rather than deleted, because the evaluator (`rotation.ts`) and the
     * label layer (`rotationCorrectChipLabel`) already support it, and a mothballed block is
     * meant to be skipped by DECISION — half-removing a capability is exactly the silent rot
     * `MOTHBALLED_BLOCK_TYPES` warns about. See the note there: rotation comes back when its
     * turn-count grading model is built, and that is also what the preset's band would mean.
     */
    if (s.trigger === SET_ANSWER_TRIGGER) {
      const presetList = neutralChips(rotationChips(d, { trigger: 'correct' } as never, chipRoll, t, locale, targetedTriggers));
      chips.push(...toTestBarChips(presetList, {
        id: s.id, category: 'targeted', active: firedIds.has(s.id),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
      continue;
    }
    chips.push({
      id: s.id,
      condition: resolvedTriggerChip({
        trigger: s.trigger, scenario: rolledValues,
        summaryOf: resolved => rotationTargetedChipLabel(resolved, d, t, locale),
        evaluate: (expr, values) =>
          evaluateFormulaRaw(expr, values, angleModeFor(d, `feedback.${s.id}.trigger`)),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      }),
      evaluation: displayEvaluation(d.gradingMode, s.evaluation),
      active: firedIds.has(s.id),
      category: 'targeted',
    });
  }


  if (participation) {
    const catchAll = find(d.gradingMode === 'optional' ? 'any_response' : 'any_answer');
    if (catchAll) {
      /*
       * OPTIONAL is ONE ROW. Everything accumulated above — the gates, the targeted loop — belongs
       * to a mode that can fire them, and optional fires none of them: it is inert, so Any Response
       * is the only outcome it has. Cleared rather than never-pushed, because the rungs above are
       * shared with survey, which does fire them. `legendStatesFor` makes the same call for the
       * blocks that build their legend through it.
       */
      if (d.gradingMode === 'optional') chips.length = 0;

      // Open domain — any reading off a targeted one lands here — so the plain label, not an
      // enumeration. The tick goes the moment a target exists, per `catchAllChips`' own rule.
      const catchAllTargeted = visibleTargeted(states, d.gradingMode).length > 0;
      push({ ...catchAll, evaluation: catchAllTargeted ? 'neutral' : catchAll.evaluation },
        d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
        // A LABEL, not null. The sensors have always passed null here and drawn an empty chip —
        // tolerable while it was the only chip on the row, and plainly wrong now that a targeted
        // chip sits beside it saying what it catches.
        //
        // And `catchAllTitle` rather than the raw key: the tick already went neutral the moment a
        // target existed, while the NAME went on saying "Any Answer" beside it.
        catchAllTitle(d.gradingMode, catchAllTargeted, t)
          ?? t('blocks.feedbackDefaults.anyAnswerTitle'));
    }
    return { testResult, testChips: chips, hasChips: chips.length > 0 };
  }

  const incorrectState = find('incorrect');
  // See the panel's own incorrectFormulaChip: an unrolled equation gives the coverage maths nothing
  // real, and it concluded "fully covered" for a state that catches every reading.
  const unresolvedFormulaAnswer = rotationAnswerHasFormula(d) && correctFan.length === 0 && !setupDead;
  const incorrectReachable = unresolvedFormulaAnswer || !isRotationDefaultIncorrectUnreachable(acceptedRanges, targetedTriggers);
  // The same gaps the feedback panel lists, so the two panels stop describing Default Incorrect
  // differently — the legend used to say "Anything but 29 to 31" while the feedback card listed the
  // real leftovers, "0 to 29 or 31 to 360".
  const incorrectList = incorrectState ? rotationChips(d, incorrectState, chipRoll, t, locale, targetedTriggers) : [];
  const incorrectFan = (incorrectReachable && incorrectState)
    ? toTestBarChips(incorrectList, barEnvelope(incorrectState, 'incorrect', incorrectList))
    : [];
  if (incorrectFan.length > 0) chips.push(...incorrectFan);
  else push(incorrectState, 'incorrect', null, incorrectReachable);

  return { testResult, testChips: chips, hasChips: chips.length > 0 };
}

function firedFeedbackEvalClass(evaluation: FeedbackState['evaluation']): string {
  return chipToneClass(evaluation);
}
/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderRotationFiredFeedback(testResult: FeedbackState[] | null): React.ReactNode {
  return <FiredFeedback states={testResult} className='mt-3 flex flex-col gap-2' />;
}

/**
 * How far the phone may twist off the plane it started in before the reading is called into
 * question. The tracker reports drift off the pivot plane; past this, the rotation being integrated
 * is not the rotation the pivot describes and the angle has started to foreshorten.
 *
 * Eight degrees is deliberately tighter than the point where the number goes visibly wrong (a 10
 * degree lean costs only about 0.4 degrees of angle), so the warning lands before the reading does.
 */
// The live warning and the grading gate read the SAME number, from the evaluator that owns it.
// Two copies of one threshold is how a student gets warned at 8 and graded at something else.
const ROTATION_OFF_AXIS_WARN_DEG = ROTATION_OFF_AXIS_CUTOFF_DEG;

/**
 * `min-w` matters as much as the type here. This readout shares a `flex-wrap` row with Calibrate and
 * Capture, so its WIDTH decides whether that row fits on one line — and a placeholder two characters
 * wide growing into a six-character reading was enough to rewrap the row and shove everything below
 * it down the page. Reserving the widest reading ("359.9°", six characters, and monospace with
 * tabular figures so a character is a fixed width) keeps the row's shape constant whatever it shows.
 */
const ROTATION_READOUT_CLASS = 'inline-block min-w-[6ch] select-none text-center font-mono text-5xl tabular-nums';
/**
 * Committed or not, in the one place a student is looking.
 *
 * There was no tell: a live reading and a captured one were the same black number, so the only
 * evidence that Capture had done anything was that the figure stopped moving — which on a phone
 * held still is no evidence at all. Muted while the instrument is still reading, full strength once
 * it is the student's answer.
 */
/**
 * WEIGHT as well as colour, because colour alone was too quiet to notice.
 *
 * A live reading is what the instrument currently sees; a captured one is the student's answer, and
 * the difference between those two is the whole point of pressing the button. Dropping the weight
 * as well as the tone makes the commit visible at a glance without making the live figure hard to
 * read — which a lighter grey on its own would have, at the size this renders.
 */
const ROTATION_READOUT_LIVE = 'font-normal text-ws-muted/80';
const ROTATION_READOUT_CAPTURED = 'font-semibold text-ws-text';
// Shared by the live capture button and its inactive twin on the unselected card, so the two can only
// ever look the same — see RotationAnswerDisplay for why the inactive one is a `span`.
const ROTATION_CAPTURE_BUTTON_CLASS = 'flex w-fit items-center gap-2 rounded-lg bg-ws-primary px-5 py-2 text-sm font-medium text-ws-btn-on-primary';

/**
 * The calibrate/capture gesture's presentation state.
 *
 * Every string arrives pre-translated. The block computes them because deciding which one applies
 * needs the tracker snapshot and the sensor status, neither of which the display component sees —
 * and threading both down just so it could call `t` itself would put measurement logic inside a
 * component whose whole job is to be the student surface, unmodified, on three different screens.
 */
interface RotationAdvisoryCopy {
  /** Names the condition, carrying its own punctuation. Deliberately reuses the words the simulate
   * dropdown uses for the same fault, so what a teacher arms and what a student reads are one
   * vocabulary — the same tie GPS and Compass make between their scenario, advisory and feedback
   * wording. */
  label: string;
  advice: string;
}

interface RotationCalibrationUi {
  calibrated: boolean;
  onCalibrate: () => void;
  /** A refused calibration. */
  error: RotationAdvisoryCopy | null;
  /** Why the block is on the simulator rather than the sensor. A plain device statement with no
   * separable condition to name, so no label. */
  notice: string | null;
  /** What the gesture is doing right now. Not a warning. */
  hint: string | null;
  /** Something wrong with the gesture in progress. */
  warning: RotationAdvisoryCopy | null;
}

/**
 * THE student surface: exactly what a student sees and does, and nothing else.
 *
 * Same contract as `GpsAnswerDisplay`/`CompassAnswerDisplay` — the live-test card, Preview and the (not
 * yet built) student runtime all render THIS, unmodified, so anything added here reaches students and
 * anything a student must never see belongs in `RotationTestWidget`. Order is the student's reading order:
 * question, then the readout and its capture control, then whatever feedback fired.
 */
function RotationAnswerDisplay({
  d, reading, hasReading, liveReading, onTakeReading, t, locale, firedFeedback, disabled, bypass, calibration,
}: {
  d: RotationBlockEditorData;
  /** The CAPTURED answer — what gets evaluated. Null until Capture is pressed. */
  reading: number | null;
  hasReading: boolean;
  /** The angle being read RIGHT NOW, independent of any capture.
   *
   * The real gravity sensor when the device has one and the gesture has started, the teacher's armed
   * simulator scenario otherwise. Both arrive here as a plain number, so this component cannot tell
   * them apart and does not need to.
   *
   * The gesture is unperformable without a live number: a student sweeping to 47° cannot know they
   * have arrived if the readout is blank until they capture, so they would be committing blind. The
   * design calls for "a large live-read number" between the two buttons and uses it as the
   * calibration sanity check ("should show ~0° right after Calibrate if they are actually flat").
   *
   * The value stays ABSOLUTE across a calibration: Calibrate is not a zero-point, so calibrating at
   * 45° reports 45°, not 0°. What it buys is resolution of the 0-180° fold-back, which only shows up
   * in readings past 180°. */
  liveReading?: number | null;
  onTakeReading?: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  firedFeedback?: React.ReactNode;
  /** Non-interactive summary for the unselected canvas card: same markup, nothing clickable. */
  disabled?: boolean;
  /** The student's typed fallback answer. Absent only on the unselected card, which renders the
   * box as an inert span (a real input cannot live inside the card's activation button).
   * `answer` is the same value parsed and normalized, or null — it drives the READOUT, not just the
   * evaluation, so a student who types their answer sees it where the sensor's would have been. */
  bypass?: { value: string; onChange: (v: string) => void; invalid: boolean; answer: number | null };
  /** The calibrate/capture gesture state. Absent on the unselected card, which is inert.
   *
   * Angle is ALWAYS a gesture block (recorded decision): a single gravity-vector read is confined to
   * 0-180° and silently mirrors anything beyond it — a true 250° reads as 110°, with no error. Only a
   * tracked rotation path from a known start resolves that, which is what Calibrate opens. So Capture
   * is gated on a live calibration rather than merely styled as second in line. */
  calibration?: RotationCalibrationUi;
}) {
  // `exact` only: a tolerance around a hidden angle discloses precision, while a `between` range or
  // a threshold IS the answer. Absolute always — Angle hardcodes `toleranceMode: 'absolute'`, since a
  // percentage of an angle is meaningless on a loop.
  const { i18n } = useTranslation();
  const firstAnswer = d.answers[0];
  const sameTolerance = d.answers.every(a =>
    a.toleranceValue === firstAnswer?.toleranceValue && a.toleranceAbove === firstAnswer?.toleranceAbove
    && a.toleranceBelow === firstAnswer?.toleranceBelow);
  // See `effectiveSetting`: a mode that takes the setting away must also take its effect away.
  const toleranceHintText = effectiveSetting('showTolerance', d.gradingMode, d.showTolerance)
    && d.conditionType === 'exact' && firstAnswer && sameTolerance
    ? toleranceHint(
        { symmetry: answerSymmetry(firstAnswer, d), toleranceValue: firstAnswer.toleranceValue,
          toleranceAbove: firstAnswer.toleranceAbove, toleranceBelow: firstAnswer.toleranceBelow },
        { fmt: (n: number) => fmtLocale(n, i18n.language), t, unit: '°' })
    : null;
  const bypassAnswer = bypass?.answer ?? null;
  /** Whether what is on screen is the student's ANSWER rather than the instrument still reading. */
  const captured = bypassAnswer != null || hasReading;
  return (
    <div
      className={`flex flex-col gap-3 ${disabled ? 'pointer-events-none' : ''}`}
      aria-hidden={disabled || undefined}
    >
      <BlockQuestion html={d.question} />

      {/* GUIDANCE, so it sits with the instruction and above the instrument, never after a reading.
          Same placement and italic treatment as GPS and Compass. */}
      {toleranceHintText && <p className={`text-center ${TOLERANCE_HINT_CLASS}`}>{toleranceHintText}</p>}

      {/* Everything below the question dims on the collapsed card: the reading shown there is
          representative, not a live fix. The question is NOT dimmed — it is the same authored
          text on every surface, and dimming it also faded its variable chips to 50% in these
          three blocks and no others. */}
      <div className={`flex flex-col gap-3 ${disabled ? 'opacity-50' : ''}`}>

        <div className='mx-auto flex w-fit flex-col items-center gap-2'>
          {/* Calibrate — live readout — Capture, side by side and in that order, per the gesture-capture
              design: an Angle reading is a MOTION (lay flat, Calibrate, rotate in one continuous sweep,
              Capture), not the phone's static tilt. Calibrate opens a short gyroscope-integration window,
              which is what resolves the 0-180 fold-back a single gravity-vector read cannot (a true 250°
              and a 110° produce the identical angle-from-vertical). It is therefore REQUIRED, not
              optional — Angle is always a gesture block. Capture stays visually weaker than Calibrate to
              hint at the required order. Neither does anything yet: real DeviceMotion capture lands with
              the student runner, and this is the settled visual reference it will be built against.
              flex-wrap, not a fixed row: three items at 48px type overflowed a tablet-width card
              (measured 321px of content in a 394px column, clipped by 24px, 39px of sideways scroll).
              Wrapping degrades to two centred lines instead — a display state you have to scroll to read
              defeats the point of it being a spot-check. */}
          <div className='flex flex-wrap items-center justify-center gap-3'>
            {/* A REAL button now, not a decorative span. It was inert on every surface, so the control
                the gesture REQUIRES first was the one control that did nothing — and a teacher had no
                way to see the order the student is meant to follow. Still a span on the unselected
                card, which mounts inside that card's own activation button. */}
            {calibration ? (
              <button
                type='button'
                onClick={calibration.onCalibrate}
                aria-pressed={calibration.calibrated}
                className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                  calibration.calibrated
                    ? 'border-ws-success-fg bg-ws-success-bg text-ws-success-fg'
                    : 'border-ws-border bg-ws-btn text-ws-text hover:border-ws-btn-active-fg'
                }`}
              >
                {/* "Recalibrate", not "Calibrated": the button stays live on purpose — a student who
                    calibrated at the wrong moment, or whose window expired, must be able to start the
                    gesture again. Labelling it with the achieved STATE made it read as terminal. */}
                {t(calibration.calibrated ? 'blocks.rotation.recalibrateButton' : 'blocks.rotation.calibrateButton')}
              </button>
            ) : (
              <span className='rounded-lg border border-ws-border bg-ws-btn px-4 py-2 text-sm font-medium text-ws-text opacity-60'>
                {t('blocks.rotation.calibrateButton')}
              </span>
            )}
            {/* A typed bypass wins over the sensor here exactly as it does in the evaluation. Showing
                the instrument's angle while grading the typed one would tell a student their answer is
                something other than what they entered. */}
            {/* Priority: a typed bypass, then the captured answer, then the live instrument value.
                Frozen on capture rather than left updating — after committing, the student needs to see
                WHAT they committed, not the angle their hand has drifted to since. */}
            {/*
              NOT a live region — see `SensorAnnounce`, which is.
              
              The attribute used to sit here, on the reasoning that this span is always mounted so a
              capture has somewhere to land. True, and beside the point: the span also holds
              `liveReading`, which during a tracked gesture changes every integration frame. A
              reader was handed one polite announcement per frame of a rotation, and arrowing away
              did not stop it. Rotation is the worst-affected of the four blocks because its reading
              is a MOTION, so the stream only exists while the value is moving fastest.
            */}
            <span className={`${ROTATION_READOUT_CLASS} ${captured ? ROTATION_READOUT_CAPTURED : ROTATION_READOUT_LIVE}`}>
              {bypassAnswer != null ? `${fmtDegrees(bypassAnswer, locale)}°`
                : hasReading && reading != null ? `${fmtDegrees(reading, locale)}°`
                : liveReading != null ? `${fmtDegrees(liveReading, locale)}°`
                : <SpokenPlaceholder>{t('blocks.rotation.liveReadPlaceholder')}</SpokenPlaceholder>}
            </span>
            {/* The announcement, and the only live region here: silent while the angle is merely
                being tracked, speaking once when a capture commits one. A typed bypass is excluded
                because the student's own input announces itself as they type it. */}
            <SensorAnnounce
              value={hasReading && reading != null ? `${fmtDegrees(reading, locale)}°` : null}
            />
            {/* Rendered as a `span` rather than a `<button disabled>`: the unselected card mounts this
                INSIDE the card's own activation button, and a nested <button> is invalid HTML that
                browsers may reparent. */}
            {disabled ? (
              <span className={`${ROTATION_CAPTURE_BUTTON_CLASS} opacity-30`}>
                <RotationIcon className='h-4 w-4' />
                {t('blocks.rotation.captureButton')}
              </span>
            ) : (
              // Not gated on the answer setup, matching Compass and GPS. Capturing is not
              // answer-dependent, and this button is on the STUDENT surface, where an unauthored
              // block is unreachable — the gate only ever blocked the teacher testing a draft.
              <button
                type='button'
                onClick={onTakeReading}
                // Gated on a LIVE calibration, not merely styled as second in line. Without a tracked
                // rotation path a capture beyond 180° is silently mirrored, and the student cannot tell.
                disabled={!!calibration && !calibration.calibrated}
                title={calibration && !calibration.calibrated ? t('blocks.rotation.captureNeedsCalibration') : undefined}
                className={`${ROTATION_CAPTURE_BUTTON_CLASS} transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40`}
              >
                <RotationIcon aria-hidden='true' className='h-4 w-4' />
                {/* "Capture Again" once there is something to replace, the same way Calibrate
                    becomes Recalibrate — and for the same reason: the control has to stay live for a
                    student who committed the wrong reading, and a label that changes says so without
                    a second control. */}
                {t(captured ? 'blocks.rotation.captureAgainButton' : 'blocks.rotation.captureButton')}
              </button>
            )}
          </div>
          {/* One line at a time, most urgent first, so the gesture never carries a stack of advice.
              A refusal outranks everything (the student did something the sensor rejected); then a
              live warning; then the instruction to calibrate; then commentary on a healthy gesture.
              The sensor notice sits outside that ladder because it explains the READOUT's provenance
              rather than the gesture's state, and a teacher on a computer needs it alongside whatever
              else is showing. */}
          {/* Anything WRONG goes through SensorAdvisory, so an Angle problem carries the same triangle
              and the same warning colour as GPS's and Compass's. A refused calibration used to be
              error-purple and the tracking problems plain orange text, which made three treatments of
              one idea across three sensor blocks. Instructions are not warnings and stay plain. */}
          {calibration?.error ? (
            <SensorAdvisory label={calibration.error.label} advice={calibration.error.advice} />
          ) : calibration?.warning ? (
            <SensorAdvisory label={calibration.warning.label} advice={calibration.warning.advice} />
          ) : calibration ? (
            // The instruction ADVANCES rather than disappearing. It used to be shown only while
            // uncalibrated, so the moment a student got the first step right the guidance vanished and
            // left them with two buttons and no statement of what to do next — and in simulate mode
            // nothing replaced it at all, since the tracking commentary below only exists in device
            // mode. Calibrating is the start of the gesture, not the end of needing to be told it.
            <p className='text-center text-xs text-ws-muted'>
              {t(calibration.calibrated ? 'blocks.rotation.captureAfterCalibration' : 'blocks.rotation.captureNeedsCalibration')}
            </p>
          ) : null}
          {/* Live tracking commentary, a separate register from the instruction above: that one says
              what to do, this says what the sensor is currently seeing. Device mode only. */}
          {calibration?.hint && !calibration.error && !calibration.warning && (
            <p className='text-center text-xs text-ws-muted'>{calibration.hint}</p>
          )}
          {calibration?.notice && <SensorAdvisory advice={calibration.notice} />}
          {/* The manual-entry fallback a teacher can enable, shown on EVERY surface when enabled — not
              just the unselected card. Gating it on `disabled` meant a teacher who ticked the setting saw
              no trace of it in test or Preview, which is the divergence this component exists to prevent.

              A REAL input, ported from Compass. It was a static span placeholder deferred to "the
              student runner", which left the one affordance for a student whose sensor doesn't work as
              the one thing a teacher could not try. Angle needs it at least as much as Compass does:
              its reading is a MOTION requiring a gyroscope, so any device without one, or any student
              who cannot perform the sweep, has no other way to answer. On the unselected card it
              degrades to a span, for the same reason the capture button does. */}
          {d.bypassEnabled && (
            <SensorManualBypass
              label={t('blocks.manualBypass.label')}
              quantityLabel={t('blocks.rotation.manualBypassQuantity')}
              value={bypass?.value ?? ''}
              onChange={bypass?.onChange ?? (() => {})}
              invalid={bypass?.invalid ?? false}
              invalidMessage={t('blocks.manualBypass.outOfRange')}
              inert={!bypass}
            />
          )}
        </div>

        {firedFeedback}
      </div>
    </div>
  );
}

/** The teacher's test harness wrapped around `RotationAnswerDisplay` — see CompassTestWidget's own doc
 * comment. The Simulate picker is teacher-only and must stay out of the answer display; it also shows
 * in Preview, because Angle has no sensor capture implemented at all (the tilt-gesture capture is still
 * a design), so simulation is currently the only way to read this block anywhere. */
function RotationTestWidget({
  d, scenarioId, onScenarioChange, mode, onModeChange, deviceStatusHint, reading, hintReading, liveReading, manual, onTakeReading, hasReading, t, locale, bypass, calibration, children, sharedDevice,
}: {
  d: RotationBlockEditorData;
  /** `null` when no fault is armed — the normal case, where the reading is whatever is typed. */
  scenarioId: RotationScenarioId | null;
  onScenarioChange: (id: RotationScenarioId | null) => void;
  reading: number | null;
  /** Ungated: what the armed selection WOULD report, for the always-visible hint. Angle withholds
   * every reading until Capture, so a hint gated on `reading` was blank exactly when the teacher was
   * choosing a scenario — the choice looked like it did nothing. Same fix Compass and GPS got. */
  hintReading: number | null;
  /** What the instrument reads right now, shown continuously so the sweep can be aimed. */
  liveReading: number | null;
  /** Typed angle for the simulate strip, the escape hatch a fixed preset list cannot cover — and the
   * only way to drive this block at all on a machine with no motion sensors. */
  manual: { value: string; onChange: (v: string) => void; invalid: boolean };
  onTakeReading: () => void;
  hasReading: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  bypass: { value: string; onChange: (v: string) => void; invalid: boolean; answer: number | null };
  calibration: RotationCalibrationUi;
  mode: SensorSourceMode;
  onModeChange: (next: SensorSourceMode) => void;
  /** What the motion sensor is doing, or why it cannot be read here. Only consulted in device mode. */
  deviceStatusHint: string;
  /**
   * Preview drives this block from the task's ONE phone, so the per-block strip comes out: three
   * strips in Preview model three phones, which is the thing the shared simulator exists to stop.
   * The strip stays in the EDITOR, where a teacher is testing one block against its own answer in
   * isolation and that is exactly what they want.
   */
  sharedDevice?: boolean;
  children?: React.ReactNode;
}) {
  const setupIncomplete = rotationSimBase(d) == null;
  // An existing reading is described first, ahead of the incomplete-setup notice — a typed angle
  // produces one with no answer authored, and telling a teacher to "set an answer" while the strip
  // is showing them their own value contradicts what is on screen.
  const hint = mode === 'device' ? deviceStatusHint
    : hintReading != null
    ? t('blocks.rotation.simulateWillRead', { value: `${fmtDegrees(hintReading, locale)}°` })
    : scenarioId != null ? t(`blocks.rotation.simulateFaultHint.${scenarioId}`)
    : setupIncomplete ? t('blocks.rotation.simulateNeedsAnswer')
    : '';
  return (
    <div className='flex flex-col gap-3'>
      {!sharedDevice && (
      /* The same strip Compass and GPS use. The Device tab reads the real gravity sensor through
         `useRotationSensor`; before it existed, a live sensor simply overrode the simulator, which left
         a teacher authoring on a phone with no way to reach these faults at all. */
      <SensorSimulateBar
        groupLabel={t('blocks.sensorSource.scenarioGroup')}
        scenarios={rotationScenarioIds().map((id): SensorScenarioOption => ({
          id,
          label: t(`blocks.rotation.simulateScenario.${id}`),
          suppressesValue: rotationFaultSuppressesValue(id),
        }))}
        activeScenarioId={scenarioId}
        onPickScenario={id => onScenarioChange(id === '' ? null : (id as RotationScenarioId))}
        // Never gated on the answer setup any more: every remaining option is a fault that needs
        // nothing from it, and the manual box is a literal.
        mode={mode}
        onModeChange={onModeChange}
        deviceTabLabel={t('blocks.sensorSource.device')}
        simulateTabLabel={t('blocks.sensorSource.simulate')}
        manual={{
          label: t('blocks.rotation.simulateManualLabel'),
          active: manual.value.trim() !== '' && !manual.invalid,
          fields: [{
            key: 'rotation',
            disabled: scenarioId != null && rotationFaultSuppressesValue(scenarioId),
            ariaLabel: t('blocks.rotation.simulateManualAriaLabel'),
            value: manual.value,
            onChange: manual.onChange,
            invalid: manual.invalid,
            suffix: '°',
          }],
        }}
        hint={hint}
      />
      )}

      <RotationAnswerDisplay
        d={d}
        reading={reading}
        hasReading={hasReading}
        onTakeReading={onTakeReading}
        liveReading={liveReading}
        t={t}
        locale={locale}
        bypass={bypass}
        calibration={calibration}
        firedFeedback={children}
      />
    </div>
  );
}

function rotationCollapsedBar(
  d: RotationBlockEditorData,
  /** Nodes, not a string: a formula answer renders through `FormulaWithValues` so the variable
   * appears as a CHIP, the same as numerical's and slider's answer bands. A plain string would put
   * the literal characters "{angle}" on the card. */
  correctSummary: React.ReactNode,
  t: (k: string, opts?: Record<string, unknown>) => string,
  issues: BlockIssue[],
): React.ReactNode {
  // Informational-only, no enforcement — angle is equally a phone-sensor block, shown on
  // every state of this bar for consistency with GPS's own convention.
  const mobileChip = <MobileOnlyChip />;

  // The mobile chip rides along in every state, which used to be the stated reason this bar
  // couldn't call a shared renderer. It can now: `trailing` is part of the shared bar's own
  // contract, so all three sensor blocks and the six others render one implementation.
  const issueBar = collapsedIssueBar(issues, mobileChip);
  if (issueBar) return issueBar;

  if (d.gradingMode !== 'graded') return collapsedModeBar(d.gradingMode, t, 'rotation', mobileChip);

  // Raw-answer style (matches numerical/GPS's collapsed bar), not a chip/pill.
  return (
    <CollapsedBlockBar tone='success' align='start' scrollX trailing={mobileChip}>
      <span className='min-w-0'>{correctSummary}</span>
    </CollapsedBlockBar>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RotationBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  blocks, onRename,
}: RotationBlockProps) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const initial = useRef(initData(block.data));
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read the push paths depend on; a ref cannot re-render, so restored HTML needs
   * state to reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedConditionType, setConditionType] = useState(initial.current.conditionType);
  const [editedAnswers, setAnswers] = useState(initial.current.answers);
  const [editedToleranceSymmetry, setToleranceSymmetry] = useState(initial.current.toleranceSymmetry);
  const [editedShowTolerance, setShowTolerance] = useState(initial.current.showTolerance);
  const [editedBetweenAnswers, setBetweenAnswers] = useState(initial.current.betweenAnswers);
  const [editedThreshold, setThreshold] = useState(initial.current.threshold);
  // Answer equations wait for the teacher to stop typing before being called malformed — every
  // formula is invalid partway through. Declared here, above the `if (!isEditing)` branch, because
  // that branch returns: a hook after it would not run on the collapsed path. Keyed off the same
  // state `liveD` is assembled from further down, and off `block.data` for the card.
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedBypassEnabled, setBypassEnabled] = useState(initial.current.bypassEnabled);
  const [variableTestMode, setVariableTestMode] = useState<'min' | 'max' | 'random' | null>(null);
  // Numerical's model, not slider's: a scenario SEPARATE from the block-test roll. Null means no
  // scenario picked, and the feedback panel then shows the equation as chips rather than resolving
  // it — a display surface shows the variable, not one roll of it. The test bar keeps using the
  // block roll, because that IS the testing-condition line.
  const [feedbackTestValues, setFeedbackTestValues] = useState<Record<string, string> | null>(null);
  const [armedAnswerId, setArmedAnswerId] = useState<string | null>(null);
  const [armedBetweenId, setArmedBetweenId] = useState<string | null>(null);

  // ── Live test state ─────────────────────────────────────────────────────────
  // Same shape as Compass's — see its own comment. Angle has no accuracy gate, so there is no
  // separate accuracy figure to carry alongside the reading.
  const [testScenario, setTestScenario] = useState<RotationScenarioId | null>(null);
  /**
   * Device or simulator, and a hard either/or.
   *
   * Before this existed the sensor simply won whenever it was live, so a teacher authoring on a phone
   * could not reach the simulate presets at all — the one surface where testing a fault matters most.
   */
  const [sourceMode, setSourceMode] = useState<SensorSourceMode>('simulate');
  // Retained only so Reroll still redraws variables and resets the panel; no simulated reading is
  // randomised any more, since every remaining scenario is a fault with nothing to draw.
  const [, setSimNonce] = useState(0);
  /**
   * What this block handed in last time it was on screen.
   *
   * Restored unconditionally, unlike GPS and Compass. Those two have a Live mode whose readout
   * tracks the device, where putting an old reading back would be a lie about where the student
   * is now. Angle has no such mode: its reading only ever exists because someone pressed Capture,
   * so the captured angle IS the answer and belongs back on screen until they capture again.
   */
  const prior = usePriorAnswer<number>();
  const [testTaken, setTestTaken] = useState(prior !== undefined);
  /** The angle frozen by the last Capture. Test state, not block data. */
  const [capturedDegrees, setCapturedDegrees] = useState<number | null>(prior ?? null);
  const [bypassInput, setBypassInput] = useState('');
  /**
   * The real gravity sensor, plus the tracker that turns it into an angle.
   *
   * `useRotationSensor` attaches no listener until `calibrate()` is first called, so mounting this on
   * every Angle block costs nothing until a student or teacher actually starts the gesture.
   */
  /**
   * Preview's one device, if there is one above us. Null in the editor, which is what keeps this
   * block's own simulate strip behaving exactly as it always has.
   */
  const shared = useSensorSimulator();
  const deviceMode = shared ? shared.useDevice : sourceMode === 'device';
  /**
   * The shared device as a GRAVITY STREAM, not as an angle.
   *
   * This is the whole substance of joining the task-level simulator. The per-block strip returned a
   * number and the tracker never ran, so the one thing it exists for — deciding which side of the
   * 180 fold an attitude is on — could not be exercised: typing 250 asserted an overturn where
   * turning the dial past the fold derives one. Off Plane was a label; here it is a turn axis that
   * is genuinely not horizontal, and the tracker notices by itself.
   *
   * `signal` carries what a poor or absent signal means to an accelerometer, which is why this
   * block no longer needs a fault list of its own — see `noiseForSignal`.
   */
  /*
   * PREVIEW's shared attitude only.
   *
   * Keyed on `shared` alone, this fed `useRotationSensor` a live simulated stream in the EDITOR too —
   * the provider wraps the whole task page, not just the preview surface — so `sensorLive` was
   * true, `sensorDegrees` was the shared attitude's default of zero, and `liveRotationFor` returned
   * that before it ever reached this block's own strip. A capture stored 0° while the strip's own
   * hint said it would report 47°.
   */
  const simulatedGravity = useMemo(() => (isPreview && shared && !shared.useDevice ? {
    // Two of the sheet's three switchers compose the mounting; the third is where the hinge sits,
    // which gravity cannot see and so never reaches here.
    mounting: mountingFor(shared.attitude.posture, shared.attitude.orientation),
    startDegrees: shared.attitude.startDegrees,
    endDegrees: shared.attitude.endDegrees,
    // The off-plane fault has no control any more; the parameter stays in the physics, which is
    // tested, so a later simulator can reach it without rebuilding the model.
    offAxisDegrees: 0,
    interrupted: shared.attitude.interrupted,
    noise: noiseForSignal(shared.signal),
    silent: shared.signal === 'none',
  } : null), [shared, isPreview]);
  const sensor = useRotationSensor(deviceMode, simulatedGravity);
  const sensorSnapshot = sensor.snapshot;
  /**
   * Whether the TRACKER is driving, from either source.
   *
   * Was `deviceMode && sensor.status === 'live'`, which is now too narrow: a simulated stream
   * delivers samples the same way, and everything downstream — the calibration state, the
   * advisories, the reading — should run off the tracker whichever source filled it.
   */
  const sensorLive = sensor.status === 'live';
  /**
   * Whether this block still answers for itself when the tracker is not driving.
   *
   * With a shared device above us it does not, and that is the point: the device answers, INCLUDING
   * by answering nothing. A device reporting no signal must leave the block with no reading, not
   * quietly fall through to this block's own typed value — which is what made "one phone for the
   * whole task" worth building.
   */
  /*
   * Which strip is ON SCREEN, which is Preview's shared device or this block's own — not merely
   * whether a provider exists. `!shared` was the same mistaken assumption Compass and GPS made:
   * `SensorSimulatorProvider` wraps the whole task page, not just the preview surface, so in the
   * EDITOR this read false, `liveRotationFor` fell through to a null sensor reading, and a capture
   * stored nothing while the strip's own hint said "will report 47°".
   */
  const ownSimulate = !isPreview;
  /** The armed fault, or null. Only the editor's per-block strip has these; Preview's faults are
   * conditions of the shared device, so the tracker produces them rather than a scenario naming them. */
  const simFault = sensorLive || !ownSimulate ? null : testScenario;
  /**
   * The simulator's stand-in for a calibration.
   *
   * On a machine with no accelerometer — every desktop, and the case a teacher authoring a task is
   * overwhelmingly in — `sensor.calibrate()` cannot succeed, and without this the gesture would be
   * untestable: Capture is gated on a calibration, so the teacher could never reach it. Calibrate
   * therefore falls through to the simulator when there is no sensor to calibrate against. That is
   * honest rather than a fudge, because the simulate strip is already the declared harness for this
   * block, and it keeps the button doing on a computer what it will do on a phone.
   */
  const [simCalibrated, setSimCalibrated] = useState(false);
  const [calibrateError, setCalibrateError] = useState<RotationAdvisoryCopy | null>(null);

  /**
   * A live calibration, from whichever source is driving the block.
   *
   * With a real sensor this is the tracker's own state, so it falls away on its own when the sample
   * stream breaks — no timer needed. That replaces the fixed 30-second window this block used to
   * carry: the window existed to bound gyroscope drift, and there is no integration here to drift.
   * Nothing is accumulated; every reading is computed fresh from two absolute gravity samples. What
   * genuinely invalidates a gesture is a GAP in the samples, which is what the hook watches for.
   */
  /**
   * Every way the tracker ends up with nothing to report.
   *
   * `offAxisDegrees` is the tracker's ONE quality signal, so it is the only poor-data case — but it
   * is not the only FAILURE. Two more produce no reading at all and were falling through to a plain
   * No Answer, which is the exact unfairness this work exists to close:
   *
   *   - a broken sample stream, which drops `calibrated` (see below) so the student cannot capture
   *     until they recalibrate;
   *   - a refused calibration — screen face-down, or a sample with no usable direction.
   *
   * `gestureMarginDegrees` is deliberately absent: its own doc says it is a confidence figure for
   * the gesture CLASSIFICATION and explicitly not a validity check. So is `turns`, which describes
   * the path rather than the attitude this block measures.
   */
  const rotationSensorDead = sensorLive
    ? sensorSnapshot?.state === 'stream-broken' || calibrateError != null
    : testScenario === 'noReading' || simFault === 'trackingLost';

  const calibrated = sensorLive
    ? sensorSnapshot != null && (sensorSnapshot.state === 'awaiting-motion' || sensorSnapshot.state === 'tracking')
    // A simulated interruption drops the calibration exactly as the real one does, so the fault
    // exercises the student-facing recalibrate prompt rather than only changing a number.
    : ownSimulate && simCalibrated && simFault !== 'trackingLost';

  async function handleCalibrate() {
    setCalibrateError(null);
    const result = await sensor.calibrate();
    if (result.ok) { setSimCalibrated(true); return; }
    if (result.reason === 'face-down') {
      // The one posture the tracker refuses: the anchor's own reading would sit past the fold and
      // silently offset everything after it. Do NOT fall through to the simulator here, or a real
      // refusal on a real phone would look like a success.
      setCalibrateError({
        label: t('blocks.rotation.calibrateFaceDownLabel'),
        advice: t('blocks.rotation.calibrateFaceDown'),
      });
      return;
    }
    // 'no-signal' means there is nothing to read, i.e. no sensor on this machine. This block's own
    // simulator takes over so the rest of the gesture stays exercisable.
    //
    // NOT when a shared device is driving: there, no signal is a state the teacher chose, and
    // pretending to calibrate against it would make the one fault they armed look like it worked.
    if (ownSimulate) setSimCalibrated(true);
  }

  /**
   * Why the block is on the simulator rather than the sensor, when a teacher would otherwise be left
   * wondering whether the readout is real. Deliberately silent in the `idle` case: before anyone has
   * pressed Calibrate there is nothing to explain.
   */
  const deviceStatusHint =
    sensor.status === 'insecure-context' ? t('blocks.rotation.sensorInsecure')
    : sensor.status === 'denied' ? t('blocks.rotation.sensorDenied')
    : sensor.status === 'unsupported' ? t('blocks.rotation.sensorUnsupported')
    : sensorLive ? t('blocks.rotation.sensorLive')
    : t('blocks.rotation.sensorWaiting');
  /** Repeated under the gesture itself, not just on the strip's hint line, because the student
   * surface is the thing being judged and a readout with no provenance is the confusing part. */
  const sensorNotice = deviceMode && !sensorLive && sensor.status !== 'idle' && sensor.status !== 'requesting'
    ? deviceStatusHint
    : null;

  /**
   * Anything wrong with the gesture in progress.
   *
   * A broken stream belongs HERE rather than in the commentary below, even though it is a tracker
   * state like the others: it drops `calibrated`, so the display would otherwise fall through to the
   * generic "Calibrate first" prompt and the specific reason would never be shown. Its own copy
   * already tells the student to calibrate again, so nothing is lost by outranking that prompt.
   */
  const streamBrokenCopy = {
    label: t('blocks.rotation.streamBrokenLabel'),
    advice: t('blocks.rotation.streamBroken'),
  };
  const offAxisCopy = {
    label: t('blocks.rotation.offAxisLabel'),
    advice: t('blocks.rotation.offAxis'),
  };
  const trackingWarning: RotationAdvisoryCopy | null = !sensorLive
    ? (simFault === 'trackingLost' ? streamBrokenCopy
      : simFault === 'offPlane' ? offAxisCopy
      : null)
    : !sensorSnapshot ? null
    : sensorSnapshot.state === 'stream-broken' ? streamBrokenCopy
    : sensorSnapshot.state !== 'tracking' ? null
    // Deliberately NOT warning on `turns`. Angle measures an ATTITUDE, not a rotation — the tracked
    // path exists only to say which side of the 180 fold the attitude is on. A student who sweeps
    // 400 leaves the phone at 40, and 40 is the right answer; the extra turn is a fact about the
    // path, which this block does not measure and the deferred Rotation block would. Warning here
    // told a student to redo a measurement that was already correct.
    : sensorSnapshot.offAxisDegrees > ROTATION_OFF_AXIS_WARN_DEG ? offAxisCopy
    : null;

  /**
   * Commentary on a healthy gesture. ONE word, for all three healthy states.
   *
   * It used to name the gesture — "Tracking the tilt", "Tracking the sweep" — and to distinguish
   * those from "Move the phone to start tracking". Tilt and sweep are our vocabulary for which
   * quantity the maths is reporting; a student holding a phone against a rock has no use for the
   * word, and telling them to move a phone they are already moving read as a fault. What they need
   * to know is that the instrument is on, which is the same sentence in every one of those states.
   */
  const trackingHint = !sensorLive || !sensorSnapshot ? null
    : sensorSnapshot.state === 'awaiting-motion' || sensorSnapshot.state === 'tracking'
      ? t('blocks.rotation.tracking')
    : null;

  const calibrationProps = {
    calibrated,
    onCalibrate: handleCalibrate,
    error: calibrateError,
    notice: sensorNotice,
    hint: trackingHint,
    warning: trackingWarning,
  };

  /** The tracker's angle, from whichever stream filled it. Null when nothing is driving it, which
   * is the editor's own simulate strip — there the scenario and the manual box still answer, and the
   * two tabs stay a genuine either/or rather than a preference. */
  const sensorDegrees = sensorLive && sensorSnapshot?.degrees != null ? sensorSnapshot.degrees : null;

  /** Scratch text for the simulate strip's manual angle box — applied as typed, never saved. Distinct
   * from `bypassInput`, which is the STUDENT's answer: this one is the teacher driving the simulator,
   * and on a machine with no motion sensors it is the only way to produce a reading at all. */
  const [simManualInput, setSimManualInput] = useState('');
  const [continuous, setContinuous] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  // Already frozen at the moment of Capture, so there is nothing to snapshot at Submit the way
  // GPS and Compass need to -- this is the number the student committed to.
  useReportAnswer(hasSubmitted ? capturedDegrees ?? undefined : undefined);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  /** This block's own variable roll, for BLOCK TEST mode only.
   *
   * Preview deliberately does NOT nest this: the task-level BlockPreviewProvider supplies one roll
   * shared by every block on the page, which is the whole point of previewing a task. Testing a
   * single block is the opposite case — its own roll, re-rollable from its own Reroll button. */
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>(() => rollVariableValues(variables));
  // Preview shows ONE task-level roll shared by every block: a task whose blocks all read {n} has
  // to agree about n, and seeding Preview from this block's own roll made them disagree. Block
  // testing keeps its private roll plus reroll, which is the whole point of it.
  const taskPreviewRoll = useTaskPreviewRoll();
  /* Recalled answers ride the same map the variables do — see `useRolledWithRecalls`. The
     equation field, the answer bar and the issue list then all read one roll. */
  const rolledValues = useRolledWithRecalls(isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues);
  // The roll a test grades against. Angles are always degrees, so the formula scope is fixed —
  // unlike numerical, there is no angleMode setting to honour here.
  const resolveAnswer = (s: string, fieldId = ''): number =>
    (s.trim() === '' ? NaN : evaluateFormulaRaw(s, rolledValues, angleModeFor(fields, fieldId)));
  // Fresh values each time the block enters a test surface, so opening a block never shows the
  // stale roll from the last time it was open. Mirrors MCQ's own isSelected/phoneTestMode effects.
  useEffect(() => {
    if (isSelected || phoneTestMode === 'test') setRolledValues(rollVariableValues(variables));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, phoneTestMode]);

  /** The angle a capture would commit, from whichever source is driving the readout. */
  function liveRotationFor(data: RotationBlockEditorData): number | null {
    return sensorDegrees ?? (ownSimulate ? simRotationFor(data) : null);
  }
  function handleTakeReading(data: RotationBlockEditorData) {
    // Frozen at the moment of capture. The simulator's value is deterministic and could safely be
    // recomputed on render, but a real sensor's cannot: by the next frame the hand has moved, and
    // recomputing would show the student a different number than the one they committed.
    setCapturedDegrees(liveRotationFor(data));
    setTestTaken(true);
    // `continuous` is a TEST-PANEL affordance, so it must not apply in Preview: Preview has no
    // TestBar to switch it off, and a student pressing capture is not submitting — they'd see the
    // answer before the page-level Submit. In Preview only previewSubmitSignal reveals feedback.
    if (continuous && !isPreview) setHasSubmitted(true);
  }
  /** A typed simulate angle, clamped into the block's own domain. Applied as typed — one numeric box
   * has nothing to parse out of a longer string, so there is no moment where the teacher has to tell
   * us they are finished, and no apply button. */
  const simManualDegrees = parseFloat(simManualInput.replace(',', '.'));
  const simManualValid = simManualInput.trim() !== ''
    && Number.isFinite(simManualDegrees) && simManualDegrees >= ROTATION_MIN && simManualDegrees <= ROTATION_MAX;
  const simManualProps = {
    value: simManualInput,
    onChange: setSimManualInput,
    invalid: simManualInput.trim() !== '' && !simManualValid,
  };
  /**
   * The reading the simulator would produce.
   *
   * A fault outranks the typed angle now, which is the reverse of the old precedence. It has to: the
   * two faults that suppress a value are the whole reason for arming them, and letting a stale number
   * in the box override "No Reading" would make the fault unreachable without first clearing the box.
   * The box is disabled in exactly those cases, so there is no contradiction on screen.
   */
  function simRotationFor(data: RotationBlockEditorData): number | null {
    if (testScenario != null) {
      return rotationSimReading(testScenario, data, simManualValid ? simManualDegrees : null);
    }
    if (simManualValid) return clampRotation(simManualDegrees);
    return null;
  }

  function handleSourceModeChange(next: SensorSourceMode) {
    setSourceMode(next);
    setCalibrateError(null);
    if (next === 'device') { void sensor.calibrate(); return; }
    // Leaving the device discards its gesture rather than freezing it, so coming back cannot capture
    // against a reference sample taken before the phone was pocketed.
    sensor.reset();
  }

  function handleTestScenarioChange(id: RotationScenarioId | null) {
    setTestScenario(id);
    setSimNonce(n => n + 1);
    if (!continuous) setHasSubmitted(false);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next && testTaken && !isPreview) setHasSubmitted(true);
  }
  function handleTestSubmit() { setHasSubmitted(true); }
  /** Another draw inside the armed scenario — see sensorSimRandom. */
  /** Another draw inside the armed scenario AND a fresh variable roll — the test panel has one
   * Reroll button and it means "give me a different randomised setup", which covers both. */
  function handleSimReroll() {
    setSimNonce(n => n + 1);
    setRolledValues(rollVariableValues(variables));
  }
  function handleTestReset() {
    setTestTaken(false);
    setHasSubmitted(false);
    // Reset means "back to the start of the gesture", and the gesture starts at Calibrate. Both
    // sources have to be cleared: the tracker's reference sample and the simulator's stand-in.
    sensor.reset();
    setSimCalibrated(false);
    setCalibrateError(null);
    setCapturedDegrees(null);
    // The bypass is the student's answer, so Reset clears it alongside the capture.
    setBypassInput('');
  }

  /** The student's typed bypass answer. Test state, not block data: it is what a STUDENT enters, so
   * it belongs with testTaken/hasSubmitted and is cleared by Reset, never persisted onto the block. */
  const bypassDegrees = parseFloat(bypassInput.replace(',', '.'));
  // Range-checked against the block's own closed domain, NOT wrapped — see the matching note in
  // compass-block.tsx. Typing 985 used to fold to 265 and score a typo as correct.
  const bypassValid = bypassInput.trim() !== ''
    && Number.isFinite(bypassDegrees) && bypassDegrees >= ROTATION_MIN && bypassDegrees <= ROTATION_MAX;
  /** Typing an answer IS the answering gesture for a student who cannot use the sensor, so it must
   * reveal feedback in continuous test mode exactly as pressing Capture does. Preview is excluded for
   * the same reason the capture handler excludes it: there, only the page-level Submit reveals
   * feedback, and a student typing their answer is not submitting it. */
  function handleBypassChange(v: string) {
    setBypassInput(v);
    if (continuous && !isPreview && v.trim() !== '') setHasSubmitted(true);
  }
  /** Taken exactly as typed. Unlike Compass there is no 360-to-0 fold: Angle's domain is CLOSED, so
   * 360 is a distinct value a teacher can author an answer at, not a synonym for 0. */
  const bypassAnswer = bypassValid ? bypassDegrees : null;
  const bypassProps = { value: bypassInput, onChange: handleBypassChange, invalid: bypassInput.trim() !== '' && !bypassValid, answer: bypassAnswer };

  // Preview's single page-level Submit — reacts to the signal CHANGING, not a per-block click,
  // reusing the same test state the live test widget already drives.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setHasSubmitted(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  const sharedToolbar = useSharedEditorToolbar();
  const { setActiveEditor, activeEditorRef } = sharedToolbar;

  // Every plain <input> in this block focuses through here — see onPlainFieldFocus's own doc
  // comment for why leaving the shared toolbar pointed at the Question editor is a real bug.
  const disconnectSharedToolbar = sharedToolbar.disconnect;
  const questionEditorRef = useRef<Editor | null>(null);

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml,
    conditionType: editedConditionType,
    answers: editedAnswers,
    toleranceSymmetry: editedToleranceSymmetry,
    showTolerance: editedShowTolerance,
    betweenAnswers: editedBetweenAnswers,
    threshold: editedThreshold,
    gradingMode: editedGradingMode,
    bypassEnabled: editedBypassEnabled,
  };
  const restored = useRestoredData<RotationBlockEditorData>({
    raw: block.data,
    isEditing,
    derive: (raw) => initData(raw as Parameters<typeof initData>[0]),
    current: { ...initData(block.data), ...edited } as RotationBlockEditorData,
    apply: (next) => {
      setConditionType(next.conditionType);
      setAnswers(next.answers);
      setToleranceSymmetry(next.toleranceSymmetry);
      setShowTolerance(next.showTolerance);
      setBetweenAnswers(next.betweenAnswers);
      setThreshold(next.threshold);
      setGradingMode(next.gradingMode);
      setBypassEnabled(next.bypassEnabled);
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.((block.data as { feedback?: unknown }).feedback);
    },
  });
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const conditionType = isEditing ? editedConditionType : restored.data.conditionType;
  const answers = isEditing ? editedAnswers : restored.data.answers;
  const toleranceSymmetry = isEditing ? editedToleranceSymmetry : restored.data.toleranceSymmetry;
  const showTolerance = isEditing ? editedShowTolerance : restored.data.showTolerance;
  const betweenAnswers = isEditing ? editedBetweenAnswers : restored.data.betweenAnswers;
  const threshold = isEditing ? editedThreshold : restored.data.threshold;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const bypassEnabled = isEditing ? editedBypassEnabled : restored.data.bypassEnabled;

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  const { settleLive, settleStored } = useIssueSettling(
    { answers: answerPositionsKey({ conditionType, answers, betweenAnswers, threshold }) },
    { answers: answerPositionsKey(initData(block.data)) },
  );

  const fields: RotationBlockEditorData = {
    question: questionHtml, conditionType, answers, toleranceSymmetry, showTolerance, betweenAnswers, threshold,
    gradingMode, bypassEnabled,
    feedback: { states: [] }, // placeholder — real feedback pushed via currentDataRef below
  };
  const currentDataRef = useRef(fields);
  currentDataRef.current = fields;

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle = positiveSlotTitleFor(gradingMode, t);

  const {
    feedback, setFeedback, resyncFeedback, updateFeedbackState, deleteFeedbackState, getTitle,
  } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      // Sensor blocks say "measurement", not "question": a student who skips one of these hasn't
      // failed to answer, they've failed to TAKE A READING, and the feedback should name the thing
      // they actually have to do. Shared across GPS/Compass/Angle so the three can't drift apart.
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
      extra: {
        [NO_SENSOR_DATA_TRIGGER]: t('blocks.sensors.noSensorDataFeedback'),
        poor_axis: t('blocks.rotation.poorAxisFeedback'),
      },
    },
    // Angle's first sensor state — see buildNormalizedRotationData, which seeds the same one for the
    // non-editing render paths. Both, or the panel and the test legend disagree about what exists.
    extraDefaults: ROTATION_SENSOR_DEFAULTS(t),
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      emit({ ...currentDataRef.current, feedback: nextFeedback });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;

  function push(overrides: Partial<RotationBlockEditorData> = {}) {
    emit({ ...currentDataRef.current, feedback, ...overrides });
  }

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleGradingMode(m: GradingMode) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, m, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(m);
    push({ gradingMode: m, feedback: { states: nextFeedbackStates } });
  }

  function handleBypassToggle() {
    const next = !bypassEnabled;
    setBypassEnabled(next);
    push({ bypassEnabled: next });
  }

  function setConditionTypeAndPush(next: RotationConditionType) {
    setConditionType(next);
    push({ conditionType: next });
  }
  /**
   * Flip ONE answer between a single tolerance and a pair, carrying its numbers across.
   *
   * Switching used to wipe the other representation — go asymmetric and the `±2` you typed was
   * gone, leaving two empty boxes that grade as an exact match. Now that the toggle is per
   * answer this is the one place it happens.
   */
  function toggleAnswerSymmetry(id: string) {
    const next = answers.map(a => {
      if (a.id !== id) return a;
      return answerSymmetry(a, restored.data) === 'symmetric'
        ? { ...a, toleranceSymmetry: 'asymmetric' as const, toleranceBelow: a.toleranceValue, toleranceAbove: a.toleranceValue }
        : { ...a, toleranceSymmetry: 'symmetric' as const, toleranceValue: a.toleranceAbove || a.toleranceBelow };
    });
    setAnswers(next);
    push({ answers: next });
  }
  function updateAnswer(id: string, patch: Partial<ExactAnswer>) {
    const next = answers.map(a => a.id === id ? { ...a, ...patch } : a);
    setAnswers(next);
    push({ answers: next });
  }
  function addAnswer() {
    const next = [...answers, makeExactAnswer()];
    setAnswers(next);
    push({ answers: next });
  }
  function removeAnswer(id: string) {
    if (answers.length <= 1) return;
    const next = answers.filter(a => a.id !== id);
    setAnswers(next);
    push({ answers: next });
  }
  function updateBetweenAnswer(id: string, patch: Partial<BetweenAnswer>) {
    const next = betweenAnswers.map(a => a.id === id ? { ...a, ...patch } : a);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function addBetweenAnswer() {
    const next = [...betweenAnswers, makeBetweenAnswer()];
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function removeBetweenAnswer(id: string) {
    if (betweenAnswers.length <= 1) return;
    const next = betweenAnswers.filter(a => a.id !== id);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function handleThresholdChange(v: string) {
    // NOT sanitised. This value comes from a FormulaField, so it may legitimately be an equation —
    // `{n}*30` — and `filterPositiveNumber` strips `{`, `}` and `*`, quietly reducing it to `30`.
    // The threshold IS the answer under lt/lte/gt/gte, and every other equation-valued answer in
    // the block accepts chips, so this one silently refusing them was a defect, not a policy.
    // Out-of-range is checked separately as an issue (`thresholdOutOfRange`).
    setThreshold(v);
    push({ threshold: v });
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = feedback.states.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /**
   * A new targeted state starts BLANK.
   *
   * It used to invent one: `lt:10`, then `lt:20`, then `lt:30`, counting up to dodge a collision
   * with a trigger already in the list. Every one of those is a real, gradeable condition the
   * teacher never authored — it fires on real student answers, shows up in Flow, and counts toward
   * coverage, all while reading as a suggestion. Numerical has seeded `exact:` (empty) all along;
   * this is the other two catching up.
   *
   * The dedupe loop went with it. It existed only to make the invented numbers distinct, and blank
   * triggers do not collide in any way that matters — states are keyed by `id`, and two unfinished
   * conditions are two unfinished conditions.
   */
  /** The card the Add button last produced, so it opens ready to type into — the owner's
   *  preference for this family, and the reason `startExpanded` exists. Keyed by mode, because
   *  the panel reorders its sections on a switch and the cards remount; a bare id would reopen
   *  the last one every time. State, not a ref: it is read during render. */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    // `=`, like every other block. It was `lt:` only because that was the type the invented
    // `lt:10` seed happened to use, which was never a decision about what a new condition means.
    const trigger = 'exact:';
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: rotationTargetedTitle(trigger, t),
      trigger, content: '',
      evaluation: currentDataRef.current.gradingMode === 'graded' ? 'incorrect' : 'neutral',
    };
    const next = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(next);
    emit({ ...currentDataRef.current, feedback: next });
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    const next = { states: rebuildStates(arrayMove(targeteds, from, to)) };
    setFeedback(next);
    emit({ ...currentDataRef.current, feedback: next });
  }

  function handleDone() {
    push();
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // Summarizes whichever condition is currently active into the same plain-text form used
  // for the collapsed answer bar and the locked Correct feedback card's chip. Unlike
  // slider-block.tsx's rawCorrectChip (which shows "value ± error"), an exact answer here
  // shows the *resolved* range (e.g. "9.8° to 10.2°") — clearer once the band can wrap
  // through the 0/360 seam ("358° to 2°" reads naturally; "0° ± 2°" would not).
  /**
   * The accepted answer, in words.
   *
   * `scenario` is the whole two-style rule in one parameter. A DISPLAY surface — the canvas card,
   * the editor at rest — passes null and sees the equation as unrolled chips, because the answer
   * genuinely is "{n}*30", not any one roll of it. The moment a teacher picks Minimum/Maximum/Random
   * the question changes to "what does it accept THIS time", and the honest answer is a band of
   * degrees. Both readings are true; which is showing is the scenario, exactly as numerical's bar
   * decides it.
   *
   * This took a bug report to find because the formula branch below returned early and never
   * consulted a scenario at all, so the bar sat on chips through every roll while the feedback
   * panel beside it resolved — one fact, two derivations, for the seventh time this week.
   */
  // A single representative angle to show in the preview/collapsed mockup's live-read
  // placeholder, standing in for what a student would actually see once answered correctly —
  // rather than a generic dash that tells a teacher nothing about what they just configured.
  // Only the first OR'd answer is shown (a live read is one number, not a list); 'between' has
  // no single authored value, so its range's midpoint is used; one-sided conditions have no
  // real "point" either, so the threshold itself stands in. Null when no real answer is
  // configured yet — the mockup falls back to the dash placeholder in that case, as before.
  function representativeCorrectValue(d: RotationBlockEditorData): number | null {
    if (isRotationSetupIncomplete(d)) return null;
    if (d.conditionType === 'exact') {
      const a = d.answers.find(a => a.value.trim() !== '');
      return a ? num(a.value) : null;
    }
    if (d.conditionType === 'between') {
      const a = d.betweenAnswers.find(a => a.low.trim() !== '' && a.high.trim() !== '');
      return a ? (num(a.low) + num(a.high)) / 2 : null;
    }
    return num(d.threshold);
  }

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // Genuinely interactive now — same widget and same evaluateRotation path the live test panel
    // uses, triggered by the page-level bottom Submit. Angle is scenario-simulated with no
    // sensor fallback at all, and unlike GPS/Compass that isn't a preview-only compromise: no
    // device-orientation capture exists in this block yet (the tilt-gesture capture is still a
    // design, not an implementation), so simulation is currently the only way to read it.
    const d = buildNormalizedRotationData(initData(block.data), t);
    const previewReading = testTaken ? (capturedDegrees ?? simRotationFor(d)) : null;
    const previewTest = computeRotationTestData(d, t, i18n.language, previewReading, hasSubmitted, bypassAnswer, resolveAnswer, rolledValues, rotationSensorDead, testScenario === 'offPlane' ? ROTATION_OFF_AXIS_CUTOFF_DEG + 1 : sensorSnapshot?.offAxisDegrees ?? null);
    /* See gps-block: the same derivation asked about the DRAFT, so Current says what would fire
       now rather than going blank until a reading is taken. */
    const draftTest = computeRotationTestData(d, t, i18n.language, previewReading, true, bypassAnswer, resolveAnswer, rolledValues, rotationSensorDead, testScenario === 'offPlane' ? ROTATION_OFF_AXIS_CUTOFF_DEG + 1 : sensorSnapshot?.offAxisDegrees ?? null);
    return (
      // No question render here — RotationAnswerDisplay owns it, so Preview cannot drift from the canvas
      // card or the phone Test tab the way it previously had.
      <div className='flex flex-col gap-3'>
        <RotationTestWidget
          d={d}
          // Preview reads the task's one device, so this block's own strip stays hidden. In the
          // editor there is no provider above us and this is false, which is what keeps per-block
          // testing exactly as it was.
          sharedDevice={!!shared}
          scenarioId={testScenario}
          mode={sourceMode}
          onModeChange={handleSourceModeChange}
          deviceStatusHint={deviceStatusHint}
          onScenarioChange={handleTestScenarioChange}
          hintReading={simRotationFor(d)}
          liveReading={liveRotationFor(d)}
          manual={simManualProps}
          calibration={calibrationProps}
          reading={previewReading}
          onTakeReading={() => handleTakeReading(d)}
          hasReading={testTaken}
          t={t}
          locale={i18n.language}
          bypass={bypassProps}
        >
          {/* See keyword-block: only the block knows which conditions are in force. */}
          <ReportTestChips chips={draftTest.testChips} states={draftTest.testResult} />
          {renderRotationFiredFeedback(previewTest.testResult)}
        </RotationTestWidget>
      </div>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    const d = buildNormalizedRotationData(initData(block.data), t);
    const previewValue = representativeCorrectValue(d);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    const collapsedIssues = settleStored(memoizedByKey(rotationIssuesCache, block.data, [t, variables], () => rotationIssues(d, issueCtx)));
    const collapsedReading = testTaken ? (capturedDegrees ?? simRotationFor(d)) : null;
    const collapsedTest = computeRotationTestData(d, t, i18n.language, collapsedReading, hasSubmitted, bypassAnswer, resolveAnswer, rolledValues, rotationSensorDead, testScenario === 'offPlane' ? ROTATION_OFF_AXIS_CUTOFF_DEG + 1 : sensorSnapshot?.offAxisDegrees ?? null);
    const rotationAriaLabel = d.question
      ? t('blocks.rotation.editAriaBaseWithQuestion', { question: d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) })
      : t('blocks.rotation.editAriaBase');

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<RotationIcon className='h-5 w-5 text-ws-primary dark:text-ws-text-link' />}
        titleLabel={t('blocks.rotation.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={rotationAriaLabel}
        answerBarSlot={() => rotationCollapsedBar(d, (
          // The collapsed card is a DISPLAY surface: roll `null`, so a variable answer shows
          // its equation as chips rather than one roll of it. Same derivation as the editor.
          <AnswerBarText
            chips={rotationChips(d, { trigger: 'correct' }, null, t, i18n.language,
              (d.feedback?.states ?? []).filter(x => x.type === 'targeted').map(x => x.trigger))}
            noValidAnswerLabel={answerExprTexts(d).length > 0 ? t('blocks.answers.noValidAnswer') : undefined}
          />
        ), t, collapsedIssues)}
        testEvaluation={collapsedTest.testResult ? (collapsedTest.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: collapsedTest.testChips,
          onReset: handleTestReset,
          // Reroll redraws the randomised value INSIDE the armed scenario — "another just-inside-
          // tolerance reading", not another scenario. It lives here rather than on the scenario control
          // because a <select> doesn't fire onChange when you re-pick what's already selected, and
          // because this is the same button that rerolls variables in every other block.
          onReroll: handleSimReroll,
          notices: (
            <IssuesPanel issues={collapsedIssues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {/* DISPLAY STATE ONLY. It reserves the space the question will fill and reads as a normal
            authoring placeholder here, but a student must never see placeholder text — so it is gated on
            !isSelected, because a selected card IS the live test surface. The blank-question warning
            itself lives on the issue bar; this is just the reserved slot. */}
        {!isSelected && !d.question && (
          <EmptyQuestionNote />
        )}
        {isSelected ? (
          // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
          // task-level provider so every block on the page resolves against one shared roll.
          <BlockPreviewProvider value={rolledValues}>
          <RotationTestWidget
            d={d}
            scenarioId={testScenario}
            mode={sourceMode}
            onModeChange={handleSourceModeChange}
            deviceStatusHint={deviceStatusHint}
            onScenarioChange={handleTestScenarioChange}
            hintReading={simRotationFor(d)}
            liveReading={liveRotationFor(d)}
            manual={simManualProps}
            calibration={calibrationProps}
            reading={collapsedReading}
            onTakeReading={() => handleTakeReading(d)}
            hasReading={testTaken}
            t={t}
            locale={i18n.language}
            bypass={bypassProps}
          >
            {renderRotationFiredFeedback(collapsedTest.testResult)}
          </RotationTestWidget>
          </BlockPreviewProvider>
        ) : (
        /* The unselected card is the SAME answer display, rendered inactive — not a hand-built
           lookalike. The old copy had drifted into showing a Calibrate button that exists NOWHERE in
           the real widget (`calibrateButton` had exactly one use, right here), advertising a control a
           student is never offered; it was also the third item that made this row overflow a
           tablet-width card. A representative correct angle stands in for a reading. The actual
           correct-answer readout lives in the answer bar (rotationCollapsedBar), not here. */
        <RotationAnswerDisplay
          disabled
          d={d}
          reading={previewValue}
          hasReading={previewValue != null}
          t={t}
          locale={i18n.language}
        />
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Validation / derived state ──────────────────────────────────────────────

  // Live editor-state snapshot — what the teacher is currently typing, not the last-pushed
  // block.data, so the issue bar and the field highlights update on the same keystroke.
  const liveD: RotationBlockEditorData = {
    question: questionHtmlRef.current,
    conditionType, answers, toleranceSymmetry, showTolerance, betweenAnswers, threshold, gradingMode, bypassEnabled, feedback,
  };

  // ── Variable testing ────────────────────────────────────────────────────────
  // Same Min/Max/Random tray numerical and slider carry, from the one shared component. Drives this
  // block's OWN roll (slider's model, not numerical's separate scenario) because the test surface
  // here already resolves through `rolledValues` — a second roll state would just be a second thing
  // to keep in sync.
  /** The live answer fields WITH their ids, so each resolves in its own angle mode. */
  const answerFieldRowsLive: { value: string; fieldId: string }[] =
    liveD.conditionType === 'exact'
      ? liveD.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
      : liveD.conditionType === 'between'
        ? liveD.betweenAnswers.flatMap(a => [
          { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }])
        : [{ value: liveD.threshold, fieldId: 'threshold' }];
  const answerFieldsLive: string[] = answerFieldRowsLive.map(r => r.value);
  const hasChipFormulas = answerFieldsLive.some(hasVariableChip);
  const answerExtrema = hasChipFormulas ? computeFormulaExtrema({
      conditionType: liveD.conditionType, answers: liveD.answers, betweenAnswers: liveD.betweenAnswers,
      threshold: liveD.threshold, toleranceMode: 'absolute', toleranceSymmetry: liveD.toleranceSymmetry, angleMode: 'degrees',
  }, variables) : null;
  function applyVariableTestMode(mode: 'min' | 'max' | 'random') {
    // Drives BOTH: the scenario the feedback panel resolves against, and the block roll the test
    // surface grades against, so the two agree while a scenario is active.
    const next = mode === 'random' ? rollVariableValues(variables)
      : answerExtrema ? (mode === 'min' ? answerExtrema.loValues : answerExtrema.hiValues)
      : null;
    if (!next) return;
    setFeedbackTestValues(next);
    setRolledValues(next);
    setVariableTestMode(mode);
  }
  /** Resolves ONLY while a scenario is picked. With none, every formula stays NaN, which is what
   * makes the feedback panel fall through to showing the equation instead of a band. */
  const resolveFeedback = (v: string, fieldId: string): number =>
    feedbackTestValues ? evaluateFormulaRaw(v, feedbackTestValues, angleModeFor(liveD, fieldId)) : (hasVariableChip(v) ? NaN : num(v));

  /**
   * Where the numbers in the chips came from — `equation → value`, the shape numerical's scenario
   * bar uses and the branching blocks' test bars used before it.
   *
   * Shown only under a live scenario. At rest the equation is already on screen with its variables
   * unrolled, and repeating it here with nothing resolved would be the same line twice.
   */
  const scenarioCalculation: React.ReactNode = !feedbackTestValues ? null : (
    <div className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs text-ws-text'>
      <span className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
        {answerFieldsLive.filter(hasVariableChip).map((f, i) =>
          <span key={i}><FormulaWithValues formula={f} rolledValues={feedbackTestValues} /></span>)}
      </span>
      {(() => {
        const resolved = answerFieldRowsLive.filter(r => hasVariableChip(r.value))
          .map(r => evaluateFormulaRaw(r.value, feedbackTestValues, angleModeFor(liveD, r.fieldId)))
          .filter(Number.isFinite);
        return resolved.length === 0 ? null : (
          <span className='font-semibold tabular-nums'>
            {' → '}{resolved.map(n => `${fmtDegrees(n, i18n.language)}°`).join(', ')}
          </span>
        );
      })()}
    </div>
  );

  const variableTestController: React.ReactNode = !hasChipFormulas ? null : (
    <VariableTestController
      mode={variableTestMode}
      onSelect={applyVariableTestMode}
      onClear={() => { setVariableTestMode(null); setFeedbackTestValues(null); }}
      extremaAvailable={!!answerExtrema}
      calculation={scenarioCalculation}
    />
  );

  const editorIssues = settleLive(rotationIssues(liveD, issueCtx));

  // ── Phone "Test" tab ────────────────────────────────────────────────────────
  const phoneReading = isPhone && testTaken ? (capturedDegrees ?? simRotationFor(liveD)) : null;
  const phoneTest = isPhone ? computeRotationTestData(liveD, t, i18n.language, phoneReading, hasSubmitted, bypassAnswer, resolveAnswer, rolledValues, rotationSensorDead, testScenario === 'offPlane' ? ROTATION_OFF_AXIS_CUTOFF_DEG + 1 : sensorSnapshot?.offAxisDegrees ?? null) : null;
  const phoneTestSlot = phoneTest ? (
    // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
    // task-level provider so every block on the page resolves against one shared roll.
    <BlockPreviewProvider value={rolledValues}>
    <RotationTestWidget
      d={liveD}
      scenarioId={testScenario}
      mode={sourceMode}
      onModeChange={handleSourceModeChange}
      deviceStatusHint={deviceStatusHint}
      onScenarioChange={handleTestScenarioChange}
      hintReading={simRotationFor(liveD)}
      liveReading={liveRotationFor(liveD)}
      manual={simManualProps}
      calibration={calibrationProps}
      reading={phoneReading}
      onTakeReading={() => handleTakeReading(liveD)}
      hasReading={testTaken}
      t={t}
      locale={i18n.language}
      bypass={bypassProps}
    >
      {renderRotationFiredFeedback(phoneTest.testResult)}
    </RotationTestWidget>
    </BlockPreviewProvider>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: handleSimReroll,
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  const acceptedRanges: RotationBoundedInterval[] = computeRotationAcceptedRanges(rotationAcceptedRangeConfig(liveD, resolveFeedback));
  /** With a formula answer and NO scenario picked, the feedback panel shows the equation — the
   * `lockedTriggerLabel` path, which `summarizeCorrect` now fills with the equation itself. A band
   * here would be a band around zero, and rerolling could never change it because nothing
   * downstream of the roll was being read. */
  const feedbackShowsEquation = rotationAnswerHasFormula(liveD) && feedbackTestValues === null;
  /** The equation as a FEEDBACK chip, not bare nodes. The collapsed card's bar and this panel want
   * different wrappers: the bar is plain text, a feedback trigger is always
   * [evaluation icon | divider | condition] — the shape every other block's chips use, and the one
   * slider's own `positiveChipNode` follows. Reusing the bar's nodes here rendered a naked formula
   * beside properly-chipped siblings. */


  // Angle's domain is the closed [0, 360] loop (see rotation.ts), so every gap already has two
  // real, finite endpoints — no more ±Infinity one-sided cases to special-case here.
  function formatRotationGap(gap: RotationInterval): string {
    return formatRotationRange(gap.lo, gap.hi, t, i18n.language);
  }

  const allTargetedTriggers = feedback.states.filter(s => s.type === 'targeted').map(s => s.trigger);
  const overlappingTriggerPairs = findRotationOverlappingFeedbackPairs(allTargetedTriggers);
  const overlappingTriggerSet = new Set(overlappingTriggerPairs.flat());

  // One deterministic assignment, the same one `getIssues` probes with, so the card and the bar
  // cannot reach different conclusions about the same condition.
  const cardProbeValues = probeRoll(variables);

  /**
   * The card's ERROR — a condition that is broken, not merely awkward.
   *
   * Same detector the issue bar counts, so the two cannot disagree. They were not connected at all
   * before: the bar named the fault for a block with several conditions in it and no card carried a
   * mark, leaving the teacher to open each one and compare.
   */
  function rotationFeedbackError(trigger: string, stateId: string): string | undefined {
    const fault = triggerFault(trigger, {
      resolve: (v: string) => evaluateFormulaRaw(v, cardProbeValues, angleModeFor(fields, `feedback.${stateId}.trigger`)),
      declared: new Set(variables.map(v => v.name)),
      probeable: new Set(Object.keys(cardProbeValues)),
      allowWrap: false,
    });
    return fault ? triggerFaultMessage(fault, t) : undefined;
  }

  function rotationFeedbackWarning(trigger: string): string | undefined {
    if (isRotationFeedbackFullySubsumed(trigger, acceptedRanges)) return t('blocks.rotation.feedbackSubsumedWarning');
    if (doesRotationFeedbackOverlapAccepted(trigger, acceptedRanges)) return t('blocks.rotation.feedbackOverlapsCorrectWarning');
    if (overlappingTriggerSet.has(trigger)) return t('blocks.rotation.feedbackOverlapWarning');
    return undefined;
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────

  const unansweredState = feedback.states.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  /* The sensor states, in the order evaluateRotation gates them: nothing measured, then measured
     about the wrong axis. Angle had neither until now. */
  const noSensorDataState = feedback.states.find(s => s.trigger === NO_SENSOR_DATA_TRIGGER);
  const poorAxisState = feedback.states.find(s => s.trigger === 'poor_axis');
  const noSensorDataCards = noSensorDataState ? [
    <SensorFeedbackCard key='no-data' state={noSensorDataState} getTitle={getTitle}
      label={t('blocks.sensors.noSensorDataFeedback')}
      onChange={patch => updateFeedbackState(noSensorDataState.id, patch)}
      activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />,
  ] : [];
  const poorSensorCards = poorAxisState ? [
    <SensorFeedbackCard key='off-axis' state={poorAxisState} getTitle={getTitle}
      label={t('blocks.rotation.feedbackTriggerPoorAxis', { cutoff: fmtLocale(ROTATION_OFF_AXIS_CUTOFF_DEG, i18n.language) })}
      onChange={patch => updateFeedbackState(poorAxisState.id, patch)}
      activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />,
  ] : [];
  // Mode-scoped: a NAMED trigger authored in one mode is hidden, not deleted, in the modes it
  // cannot fire in — see `targetedFiresIn`. Value triggers fire in every mode and all stay.
  const targetedStates = visibleTargeted(feedback.states, gradingMode);
  const canAddMore = targetedStates.length < 5;

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, targetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={targetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {targetedStates.map(s => {
            const ORDER = ['neutral', 'incorrect'] as const;
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                titleLocked
                /* Neutral and locked outside graded: the stored evaluation mirrors a verdict
                   the mode has not got, and there is no incorrect to cycle TO. */
                title={s.title} content={s.content}
                evaluation={displayEvaluation(gradingMode, s.evaluation)}
                evalLocked={gradingMode !== 'graded'}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                warning={rotationFeedbackWarning(s.trigger)}
                error={rotationFeedbackError(s.trigger, s.id)}
                triggerZone={<RotationTriggerEditor fieldId={`feedback.${s.id}.trigger`} onPlainFieldFocus={disconnectSharedToolbar} trigger={s.trigger} onChange={trigger => updateFeedbackState(s.id, { trigger, title: rotationTargetedTitle(trigger, t) })} t={t} variables={variables} rolledValues={rolledValues} />}
                triggerZoneSummary={<RotationTriggerChip angleMode={angleModeFor(fields, `feedback.${s.id}.trigger`)} trigger={s.trigger} evaluation={displayEvaluation(gradingMode, s.evaluation)} t={t} scenario={feedbackTestValues ?? {}} />}
                onTitleChange={() => {}}
                onContentChange={html => updateFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                onDelete={() => deleteFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  // Correct genuinely cannot fire without a real configured answer (an unset exact/between/
  // threshold field defaults to 0 via num()) — shown with the same muted "Inactive" treatment
  // every other reachability state uses, rather than silently resolving to "0°", which would
  // misleadingly read as a real accepted angle. Same pattern as numerical-block.tsx's own
  // inactiveChip/hasConfiguredAnswer.
  const hasConfiguredAnswer = !isRotationSetupIncomplete(fields);
  const correctInactiveChip = (
    <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
  );
  const rotationPositiveInactive = gradingMode === 'graded' && !hasConfiguredAnswer;
  /** One chip per range, matching the legend and Compass's own correctChips/incorrectChips. The panel
   * used to pass a single `lockedTriggerLabel` holding every range joined into one string, so a
   * multi-answer block read as one long condition instead of the list of alternatives it is. */
  // "or" between the bands — a reading lands in exactly one of them, and the test bar names them
  // the same way (Compass's own panel chips have always been separated like this).
  const rotationFeedbackRangeChips = (ranges: RotationInterval[], evaluation: 'correct' | 'incorrect') =>
    ranges
      .filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi))
      .flatMap((r, i) => [
        ...(i > 0 ? [<span key={`${evaluation}-or-${i}`} className='self-center text-xs italic text-ws-muted'>{t('blocks.connectors.or')}</span>] : []),
        <span key={`${evaluation}-${i}`} className={`${TRIGGER_CHIP_CLASS} ${firedFeedbackEvalClass(evaluation)}`}>
          {chipEvalIcon(evaluation)}
          <ChipDivider />
          <span className={TRIGGER_CHIP_TEXT_CLASS}>
            {formatRotationRange(r.lo, r.hi, t, i18n.language)}
          </span>
        </span>,
      ]);

  // `lockedTriggerLabel` is typed `string` and cannot carry chips, so the graded case leaves it
  // undefined and puts its content through `triggerZone` instead — the same route slider's
  // `positiveChipNode` takes for exactly this reason.
  const rotationPositiveLabel =
    // Only the non-graded modes get a generic chip here; graded prints its actual answer, built
    // above. `undefined` is what lets that path stand.
    gradingMode !== 'graded' ? positiveSlotTitleFor(gradingMode, t) :
    undefined;

  // Hoisted: both default rows need the carve-outs, and this sat between them.
  const targetedTriggers = targetedStates.map(s => s.trigger);
  // `ConditionChipRow`, not `AnswerBarChips`: this is the feedback PANEL, where an unreachable
  // state is a condition being described and the Unreachable pill is the right reading. Only the
  // answer bar swaps it for bare text.
  /**
   * Outside graded the catch-all states its own name as a chip, through the shared derivation.
   *
   * It used to leave the row UNDEFINED and lean on `lockedTriggerLabel` to fill the gap, which
   * worked on the collapsed card and left the expanded one titled from storage — "Correct", on a
   * block that has no such thing. The label is the card's own title now, so the two agree.
   */
  const rotationParticipationRow = gradingMode === 'graded' ? undefined : (
    <ConditionChipRow
      chips={catchAllChips(gradingMode, targetedStates.length > 0, t, () => [{
        kind: 'chip' as const,
        label: catchAllTitle(gradingMode, targetedStates.length > 0, t) ?? '',
        evaluation: 'neutral' as const,
        sourceId: CATCH_ALL_SOURCE,
      }])}
      unreachableLabel={t('blocks.feedbackPanel.unreachable')}
    />
  );
  const correctPanelRow = rotationPositiveInactive ? correctInactiveChip
    : gradingMode !== 'graded' ? rotationParticipationRow
    : (
      <ConditionChipRow
        chips={rotationChips(liveD, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, targetedTriggers)}
        unreachableLabel={t('blocks.feedbackPanel.unreachable')}
      />
    );

  const correctSlot = feedback.states
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={rotationPositiveInactive}
        lockedTriggerLabel={rotationPositiveLabel}
        // ONE derivation, both modes. `feedbackTestValues` is already the ChipRoll — null at rest,
        // the scenario once picked — so the fork that stood here (a joined-string chip when
        // unrolled, per-range chips when rolled) is internal to `rotationChips` now.
        triggerZone={correctPanelRow}
        triggerZoneSummary={correctPanelRow}
        title={catchAllTitle(gradingMode, targetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /* The tick goes once a target exists, like every other block's catch-all. */
        evaluation={gradingMode !== 'graded' && targetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const defaultIncorrectUnreachable = isRotationDefaultIncorrectUnreachable(acceptedRanges, targetedTriggers);
  const defaultIncorrectGaps = computeRotationDefaultIncorrectGaps(acceptedRanges, targetedTriggers);
  const defaultIncorrectGapText = defaultIncorrectGaps.map(formatRotationGap).filter(Boolean).join(` ${t('blocks.connectors.or')} `);

  // With an unresolved equation there is no band to take the complement OF, so the gap maths runs
  // on whatever the formula parses to and reports a confident, wrong answer — here, that every
  // heading is covered, i.e. "Unreachable" for the state that actually catches everything. The
  // symbolic statement is the truthful one ("Anything but {n}*30"), and real bands take over the
  // moment a scenario is rolled. Same treatment as Compass, and as Numerical's own formula chip.
  // "The answer is an equation with no usable band right now" — which covers BOTH the state before
  // a scenario is rolled and a scenario that fails to resolve it. Keying only on "no scenario yet"
  // meant that rolling a formula the roll could not work out flipped the card straight back to the
  // false "Unreachable", which is the very thing being fixed.
  const answerBandUnusable = rotationAnswerHasFormula(liveD)
    && (acceptedRanges.length === 0 || acceptedRanges.some(r => !Number.isFinite(r.lo) || !Number.isFinite(r.hi)));
  const showSymbolicIncorrect = feedbackShowsEquation || answerBandUnusable;
  // ONE derivation, replacing the fork that stood here. See `rotationChips`.
  const incorrectFormulaChip: React.ReactNode = (
    <ConditionChipRow
      chips={rotationChips(liveD, { trigger: 'incorrect' }, feedbackTestValues, t, i18n.language, targetedTriggers)}
      unreachableLabel={t('blocks.feedbackPanel.unreachable')}
    />
  );
  const incorrectSlot = feedback.states
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={!showSymbolicIncorrect && defaultIncorrectUnreachable}
        lockedTriggerLabel={!showSymbolicIncorrect && !defaultIncorrectUnreachable ? defaultIncorrectGapText : undefined}
        triggerZone={showSymbolicIncorrect ? incorrectFormulaChip
          : defaultIncorrectUnreachable ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
          : defaultIncorrectGaps.length > 0 ? rotationFeedbackRangeChips(defaultIncorrectGaps, 'incorrect') : undefined}
        triggerZoneSummary={showSymbolicIncorrect ? incorrectFormulaChip
          : defaultIncorrectUnreachable ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
          : defaultIncorrectGaps.length > 0 ? rotationFeedbackRangeChips(defaultIncorrectGaps, 'incorrect') : undefined}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Editing shell ───────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      icon={<RotationIcon className='h-6 w-6' />}
      titleLabel={t('blocks.rotation.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.rotation.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
            <CheckboxRow fieldId='bypassEnabled'
              checked={bypassEnabled}
              onToggle={handleBypassToggle}
              label={t('blocks.captureMode.allowBypass')}
            />
          </OptionsSection>

          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>

            {/* Disabled when the condition type is not `exact`: a between range or a threshold IS the
                answer, so showing it would disclose what was asked rather than how precisely to do it. */}
            <CheckboxRow fieldId='showTolerance'
              checked={showTolerance}
              onToggle={() => { const v = !showTolerance; setShowTolerance(v); push({ showTolerance: v }); }}
              label={t('blocks.rotation.showTolerance')}
              disabled={conditionType !== 'exact' || !settingAvailable('showTolerance', gradingMode)}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.rotation.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      // On the FRAME, not the feedback panel: the surface that most needs the numbers
      // interpreting is the one the answer bar is hidden behind.
      scenarioBarSlot={variableTestController}
      /**
       * The accepted answer, in the EDITOR — which this block did not have.
       *
       * Both blocks built an answer bar and gave it only to the collapsed canvas card, so the one
       * surface where a teacher is actually setting the answer was the one that never showed what
       * it had become. Numerical has had an editor bar all along; this is the same content through
       * the same shared bar, minus the card's mobile chip, and it sits under the scenario bar so a
       * rolled equation and the band it resolves to read as one thing.
       *
       * Issues are NOT repeated here: `warningBarSlot` renders them directly above, which is the
       * split the collapsed card cannot make and the editor can.
       */
      answerBarSlot={fields.gradingMode !== 'graded'
        ? collapsedModeBar(fields.gradingMode, t, 'rotation')
        : (() => {
            /*
             * `isRotationSetupIncomplete` is the WRONG test for this, and gating on it is what made an
             * incomplete range show no bar at all. It conflates two states the bar must separate:
             * "nothing authored yet" (no bar) and "authored, but nothing valid" ("No Valid Answer").
             * A `between` row with one bound filled is the second — the teacher typed something and
             * the evaluator accepts nothing — and it read as the first.
             *
             * `answerExprTexts` asks the honest question: is any answer field authored at all?
             */
            const chips = rotationChips(liveD, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, targetedTriggers);
            const authored = answerExprTexts(liveD).length > 0;
            if (!answerBarHasContent(chips, authored)) return null;
            return (
              <CollapsedBlockBar tone='success' align='start' scrollX>
                <AnswerBarText chips={chips}
                  noValidAnswerLabel={authored ? t('blocks.answers.noValidAnswer') : undefined} />
              </CollapsedBlockBar>
            );
          })()}
      feedbackPanelProps={{
        // Angle's first sensor card. Nothing here before, because nothing could fire.
        noSensorDataSlot: <>{noSensorDataCards}</>,
        poorSensorSection: poorSensorCards.length > 0
          ? {
            // Named only when there is more than one, so the heading says what they share rather
            // than repeating a lone card's own label back at it.
            heading: poorSensorCards.length > 1 ? t('blocks.sensors.poorSectionHeading') : undefined,
            content: <>{poorSensorCards}</>,
          }
          : undefined,
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          isEmpty: targetedStates.length === 0,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
            <BlockQuestionEditor
              value={questionHtml}
              onChange={html => {
                // The render-time SNAPSHOT has to move with the ref, not just the ref.
                // `currentDataRef.current` is rebuilt during render, and this handler runs before any
                // re-render, so every push path would otherwise spread the PREVIOUS question and leave
                // the persisted value one keystroke behind what is on screen. MCQ/Dropdown/Keyword avoid
                // this by reading questionHtmlRef live at push time; these blocks bake the question into
                // `fields` instead, so the two have to be kept in lockstep here.
                questionHtmlRef.current = html;
                setQuestionHtml(html);
                currentDataRef.current = { ...currentDataRef.current, question: html };
                push();
              }}
              toolbar={sharedToolbar}
              editorRef={questionEditorRef}
              shouldFocusRef={shouldFocusEditor}
              embedded={embedded}
            />

            {/*
              * ANSWER SETUP, hidden in OPTIONAL only.
              *
              * Optional is the one mode with no use for an answer key. Survey keeps it: Set Answer
             * targets the authored band, so the band is live there even though nothing is graded. So the band and its tolerance have nothing to
              * act on. Hidden rather than greyed, because the whole SECTION's job is gone — the same
              * call the feedback panel already makes for its correctness cards in optional. A single
              * control that merely has no effect is disabled in place instead.
              *
              * Nothing is deleted: switch back to Has Correct and the answer is as it was.
              */}
            {gradingMode !== 'optional' && (<>
              {/* Armed-delete announcement — matches slider-block.tsx's exact pattern. */}
              <div aria-live='assertive' aria-atomic='true' className='sr-only'>
                {(armedAnswerId || armedBetweenId) ? t('blocks.actions.deleteArmed') : ''}
              </div>

              <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

              <AnswerSetupFields
                variables={variables}
                rolledValues={rolledValues}
                onPlainFieldFocus={disconnectSharedToolbar}
                t={t}
                conditionType={conditionType}
                onConditionTypeChange={setConditionTypeAndPush}
                answers={answers}
                onUpdateAnswer={updateAnswer}
                onAddAnswer={addAnswer}
                onRemoveAnswer={removeAnswer}
                onToggleAnswerSymmetry={toggleAnswerSymmetry}
                betweenAnswers={betweenAnswers}
                onUpdateBetweenAnswer={updateBetweenAnswer}
                onAddBetweenAnswer={addBetweenAnswer}
                onRemoveBetweenAnswer={removeBetweenAnswer}
                threshold={threshold}
                onThresholdChange={handleThresholdChange}
                armedAnswerId={armedAnswerId}
                setArmedAnswerId={setArmedAnswerId}
                armedBetweenId={armedBetweenId}
                setArmedBetweenId={setArmedBetweenId}
              />
            </>)}
          </div>
      }
    />
  );
}
